<template>
  <v-container fill-height fluid grid-list-xl class="pt-0">
    <v-layout justify-center wrap>
      <v-flex md12>
        <material-card color="green" class="biz-card mt-4">
          <v-flex slot="header">
            <v-layout row wrap align-center>
              <div class="biz-header-title">
                <v-icon large color="white" class="mr-2">mdi-domain</v-icon>
                <span>{{ business.name || "Business" }}</span>
                <span v-if="billingAccountID" class="biz-header-account">
                  {{ billingAccountID }}
                </span>
              </div>
              <v-spacer></v-spacer>
              <div class="biz-agent-display biz-term-switcher">
                <div class="biz-agent-display-label">Year</div>
                <div class="biz-term-list">
                  <template v-for="t in terms">
                    <span
                      v-if="t === term"
                      :key="t"
                      class="biz-term-chip biz-term-chip--active"
                      >{{ t }}</span
                    >
                    <a
                      v-else
                      :key="t"
                      href=""
                      class="biz-term-chip"
                      @click.prevent="navigateToTerm(t)"
                      >{{ t }}</a
                    >
                  </template>
                  <span v-if="!terms.length" class="biz-term-chip">{{
                    term || "—"
                  }}</span>
                </div>
              </div>
              <div class="biz-agent-display ml-3">
                <div class="biz-agent-display-label">Billing Agent</div>
                <div class="biz-agent-display-name">
                  {{ selectedAgentName || "Not set" }}
                </div>
              </div>
              <v-btn
                small
                outline
                dark
                class="ml-2 my-0"
                title="Change billing agent"
                @click="openChangeAgentDialog"
              >
                <v-icon left small>mdi-pencil</v-icon>
                Change
              </v-btn>
              <v-btn
                icon
                small
                dark
                class="ml-1 my-0"
                title="Manage Agents"
                @click="openManageAgentsDialog"
              >
                <v-icon small>mdi-cog</v-icon>
              </v-btn>
              <v-btn
                small
                outline
                dark
                class="ml-3 my-0"
                @click="openEditDialog"
              >
                <v-icon left small>mdi-pencil</v-icon>
                Edit
              </v-btn>
              <v-btn
                small
                outline
                dark
                class="ml-2 my-0"
                :disabled="hasLinkedAccounts"
                :title="
                  hasLinkedAccounts
                    ? 'Unlink all families before deleting this business'
                    : 'Delete business'
                "
                @click="openRemoveDialog"
              >
                <v-icon left small>mdi-delete</v-icon>
                Delete
              </v-btn>
            </v-layout>
          </v-flex>

          <div class="biz-body" v-if="!loading">
            <!-- ── Business info ── -->
            <div class="biz-info-row">
              <div class="biz-info-item">
                <div class="biz-label">Contact</div>
                <div class="biz-value">
                  {{ business.contactPerson || "-" }}
                  <span v-if="business.contactPersonTitle">
                    ({{ business.contactPersonTitle }})</span
                  >
                </div>
              </div>
              <div class="biz-info-item">
                <div class="biz-label">Phone</div>
                <div class="biz-value">
                  {{ formatPhone(business.phone) || "-" }}
                </div>
              </div>
              <div class="biz-info-item">
                <div class="biz-label">Email</div>
                <div class="biz-value">{{ business.email || "-" }}</div>
              </div>
              <div class="biz-info-item">
                <div class="biz-label">Address</div>
                <div class="biz-value">{{ business.address || "-" }}</div>
              </div>
              <div class="biz-info-item">
                <div class="biz-label">Billing Account</div>
                <div class="biz-value">{{ billingAccountID || "-" }}</div>
              </div>
            </div>

            <!-- ── Summary + actions ── -->
            <div class="biz-summary-grid">
              <div class="biz-summary-card biz-summary-card--wide">
                <div class="biz-summary-main">
                  <div class="biz-summary-num">
                    {{ formatCurrency(annualTotal) }}
                  </div>
                  <div class="biz-summary-label">Net Annual Contract</div>
                </div>
                <div class="biz-summary-sub">
                  <div>
                    Published Annual Charges
                    {{ formatCurrency(publishedAnnualCharges) }}
                  </div>
                  <div>
                    Contract Discount ({{ formatCurrency(contractDiscount) }})
                  </div>
                </div>
              </div>
              <div class="biz-summary-card">
                <div class="biz-summary-num">
                  {{ formatCurrency(monthlyTotal) }}
                </div>
                <div class="biz-summary-label">Monthly Total</div>
              </div>
              <div class="biz-summary-card biz-summary-card--payment">
                <div class="biz-summary-num">
                  {{ formatCurrency(totalPaid) }}
                </div>
                <div class="biz-summary-label">Total Paid</div>
              </div>
              <div class="biz-summary-card biz-summary-card--payment">
                <div class="biz-summary-num">
                  {{ formatCurrency(totalScheduled) }}
                </div>
                <div class="biz-summary-label">Total Scheduled</div>
              </div>
              <div class="biz-summary-card biz-summary-card--payment">
                <div class="biz-summary-num">
                  {{ formatCurrency(balance) }}
                </div>
                <div class="biz-summary-label">Employer Balance</div>
              </div>
            </div>

            <v-layout row wrap align-center class="biz-actions-row">
              <v-btn small color="success" @click="openLinkDialog">
                <v-icon left small>mdi-account-plus</v-icon>
                Link Family / Employee
              </v-btn>
              <v-btn small color="success" @click="openPayDialog">
                <v-icon left small>mdi-cash</v-icon>
                Make a Payment
              </v-btn>
              <v-spacer></v-spacer>
              <input
                ref="signedContractFile"
                type="file"
                accept="application/pdf,.pdf"
                style="display: none"
                @change="onSignedContractPicked"
              />
              <div
                v-if="hasSignedContract"
                class="biz-signed-pill"
                :title="signedContractTooltipFor()"
              >
                <v-icon small color="#c62828" class="mr-1"
                  >mdi-file-pdf-box</v-icon
                >
                <a
                  class="biz-signed-pill-name"
                  @click.prevent="viewSignedContract"
                  >Signed Contract</a
                >
                <v-icon small color="#2e7d32" class="ml-1"
                  >mdi-check-circle</v-icon
                >
                <span class="biz-signed-pill-divider"></span>
                <v-btn
                  icon
                  small
                  class="biz-signed-pill-btn"
                  title="View"
                  :loading="openingSignedContract"
                  @click="viewSignedContract"
                >
                  <v-icon small color="success">mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  class="biz-signed-pill-btn"
                  title="Replace"
                  :loading="uploadingSignedContract"
                  @click="pickSignedContract"
                >
                  <v-icon small color="success">mdi-swap-horizontal</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  class="biz-signed-pill-btn"
                  title="Remove"
                  @click="removeSignedContractDialog = true"
                >
                  <v-icon small color="error">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
              <v-btn
                v-else
                small
                outline
                color="success"
                class="biz-download-btn"
                :loading="uploadingSignedContract"
                @click="pickSignedContract"
              >
                <v-icon left small>mdi-paperclip</v-icon>
                Attach Signed Contract
              </v-btn>
              <v-btn
                small
                color="success"
                class="biz-download-btn"
                :loading="downloadingContract"
                @click="downloadContract"
              >
                <v-icon left small>mdi-file-document-outline</v-icon>
                Download Contract
              </v-btn>
              <v-btn
                small
                color="success"
                class="biz-download-btn"
                :loading="downloadingInvoices"
                @click="downloadInvoices"
              >
                <v-icon left small>mdi-file-multiple</v-icon>
                Download Invoices
              </v-btn>
            </v-layout>

            <v-tabs
              v-model="businessTab"
              color="transparent"
              slider-color="success"
              class="biz-section-tabs"
            >
              <v-tab>
                Linked Families
                <span class="biz-tab-count">{{ accounts.length }}</span>
              </v-tab>
              <v-tab>
                Payments
                <span class="biz-tab-count">{{ payments.length }}</span>
              </v-tab>
              <v-tab>
                Schedules
                <span class="biz-tab-count">{{ schedules.length }}</span>
              </v-tab>
              <v-tab>Ledger</v-tab>
            </v-tabs>
            <v-tabs-items v-model="businessTab">
              <v-tab-item>
            <!-- ── Linked accounts ── -->
            <div class="biz-accounts-card">
              <div class="biz-accounts-header">
                <span class="biz-accounts-title">Linked Families</span>
                <span class="biz-accounts-count">
                  {{ accounts.length }}
                  famil{{ accounts.length === 1 ? "y" : "ies" }} ·
                  {{ studentCount }}
                  covered child{{ studentCount === 1 ? "" : "ren" }}
                </span>
              </div>
              <v-expansion-panel class="biz-accounts-panel" expand>
                <v-expansion-panel-content
                  v-for="account in accounts"
                  :key="account.linkID"
                >
                  <template v-slot:header>
                    <v-layout row align-center class="pt-3">
                      <v-icon small color="#2e7d32" class="mr-2"
                        >mdi-account-group</v-icon
                      >
                      <div class="biz-account-name">
                        {{ account.accountName || account.accountID }}
                        <span class="biz-account-id"
                          >#{{ account.accountID }}</span
                        >
                      </div>
                      <v-spacer></v-spacer>
                      <div class="biz-account-total mr-3">
                        {{ account.students.length }} student(s) ·
                        {{ formatCurrency(account.annualTotal) }}/yr ·
                        {{ formatCurrency(account.monthlyTotal) }}/mo
                      </div>
                      <v-btn
                        small
                        outline
                        color="success"
                        class="my-0 biz-edit-rates-btn"
                        @click.stop="openRateDialog(account)"
                      >
                        <v-icon left small>mdi-pencil</v-icon>
                        Edit Rates
                      </v-btn>
                      <v-btn
                        small
                        outline
                        color="success"
                        class="my-0 ml-2 biz-edit-rates-btn"
                        @click.stop="openEditChildren(account)"
                      >
                        <v-icon left small>mdi-account-multiple-plus</v-icon>
                        Children
                      </v-btn>
                    </v-layout>
                  </template>

                  <v-card flat>
                    <table class="biz-students-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th style="width:90px">Student ID</th>
                          <th style="width:70px">Grade</th>
                          <th style="width:160px">Rate Type</th>
                          <th style="width:210px">Coverage</th>
                          <th style="width:90px">Annual</th>
                          <th style="width:80px">Monthly</th>
                          <th style="width:70px"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="s in account.students" :key="s.studentID">
                          <td class="biz-td-name">
                            {{ s.engName || s.hebName }}
                          </td>
                          <td>{{ s.studentID }}</td>
                          <td>{{ s.class }}</td>
                          <td>{{ rateTypeLabel(s) }}</td>
                          <td>
                            <div class="biz-coverage-cell">
                              <div>
                                {{ formatDate(s.coverageStartDate) }} –
                                {{ formatDate(s.coverageEndDate) }}
                                <div v-if="s.monthsCovered" class="biz-muted">
                                  {{ s.monthsCovered }} month(s)
                                </div>
                              </div>
                              <v-btn
                                small
                                flat
                                icon
                                color="success"
                                title="Adjust coverage dates"
                                @click="openCoverageDialog(account, s)"
                              >
                                <v-icon small>mdi-calendar-edit</v-icon>
                              </v-btn>
                            </div>
                          </td>
                          <td>
                            {{
                              formatCurrency(s.contractAmount || s.annualRate)
                            }}
                          </td>
                          <td>{{ formatCurrency(s.monthlyRate) }}</td>
                          <td>
                            <v-btn
                              small
                              flat
                              icon
                              color="error"
                              :loading="unlinkingStudentId === s.studentID"
                              @click.stop="openUnlinkStudentDialog(account, s)"
                            >
                              <v-icon small>mdi-link-off</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                        <tr v-if="!account.students.length">
                          <td colspan="8" class="biz-td-empty">
                            No covered children found for this family/term.
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div class="biz-account-details">
                      <div class="biz-label mb-2">Family details</div>
                      <v-layout row wrap class="mb-2">
                        <v-flex xs12 sm3>
                          <div class="biz-muted">Family</div>
                          <div>{{ account.accountName || "-" }}</div>
                        </v-flex>
                        <v-flex xs12 sm3>
                          <div class="biz-muted">Account</div>
                          <div>{{ account.accountID }}</div>
                        </v-flex>
                        <v-flex xs12 sm3>
                          <div class="biz-muted">Email</div>
                          <div>{{ account.email || "-" }}</div>
                        </v-flex>
                        <v-flex xs12 sm3>
                          <div class="biz-muted">Hebrew name</div>
                          <div>{{ account.accountNameH || "-" }}</div>
                        </v-flex>
                      </v-layout>
                    </div>

                    <div class="biz-invoice-rundown">
                      <div class="biz-label mb-2">Invoices</div>
                      <table
                        class="biz-students-table"
                        v-if="account.invoices && account.invoices.length"
                      >
                        <thead>
                          <tr>
                            <th>Month</th>
                            <th>Invoice No.</th>
                            <th>Lines</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="invoice in account.invoices"
                            :key="invoice.invoiceNo || invoice.serviceMonth"
                          >
                            <td>{{ formatMonth(invoice.serviceMonth) }}</td>
                            <td>{{ invoice.invoiceNo || "-" }}</td>
                            <td>
                              <div
                                v-for="(line, lineIndex) in invoice.lines || []"
                                :key="(line.studentID || '') + '-' + lineIndex"
                                class="biz-invoice-line"
                              >
                                {{ line.studentName || line.studentID }}
                                <span v-if="line.class" class="biz-muted">
                                  · {{ line.class }}</span
                                >
                                · {{ formatCurrency(line.amount) }}
                              </div>
                            </td>
                            <td>{{ formatCurrency(invoice.total) }}</td>
                          </tr>
                          <tr>
                            <td colspan="3" class="biz-td-name">
                              Family total
                            </td>
                            <td class="biz-td-name">
                              {{ formatCurrency(accountInvoiceTotal(account)) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div v-else class="biz-td-empty">
                        No saved invoices for this family yet.
                      </div>
                    </div>

                    <div class="biz-account-footer">
                      <v-btn
                        small
                        flat
                        color="error"
                        :loading="unlinkingId === account.linkID"
                        @click.stop="unlinkAccount(account)"
                      >
                        <v-icon left small>mdi-link-off</v-icon>
                        Unlink Family
                      </v-btn>
                    </div>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <div v-if="!accounts.length" class="biz-empty-accounts">
                No families are linked to this business for {{ term }} yet.
              </div>
            </div>
              </v-tab-item>

              <v-tab-item>
            <div class="biz-payments-section">
              <div class="biz-payments-title">
                <v-icon small color="#2e7d32" class="mr-2"
                  >mdi-cash-multiple</v-icon
                >
                Payments
              </div>
              <table class="biz-students-table" v-if="payments.length">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Receipt</th>
                    <th>Card</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="payment in payments"
                    :key="paymentKey(payment)"
                    class="biz-clickable-row"
                    @click="openPaymentDetails(payment)"
                  >
                    <td>
                      {{ formatPaymentDate(payment.date) }}
                      <div v-if="rowNote(payment)" class="biz-muted">
                        {{ rowNote(payment) }}
                      </div>
                    </td>
                    <td>{{ formatCurrency(payment.amount) }}</td>
                    <td>
                      {{ payment.statusValue || payment.status }}
                      <span v-if="payment.fromSchedule" class="biz-muted">
                        · scheduled
                      </span>
                    </td>
                    <td>
                      {{ payment.paymentType || payment.tranType || "-" }}
                    </td>
                    <td>
                      <span v-if="payment.receiptNumber">
                        #{{ payment.receiptNumber }}
                      </span>
                      <span v-else class="biz-muted">-</span>
                    </td>
                    <td>
                      <span v-if="payment.cardNumLast5">
                        {{ payment.cardType }} · {{ payment.cardNumLast5 }}
                      </span>
                      <span v-else class="biz-muted">-</span>
                    </td>
                    <td class="text-xs-right" @click.stop>
                      <v-btn
                        small
                        outline
                        color="success"
                        class="ma-0 mr-1"
                        @click="openPaymentDetails(payment)"
                      >
                        View Details
                      </v-btn>
                      <v-btn
                        v-if="canReverse(payment)"
                        small
                        dark
                        color="default"
                        class="ma-0"
                        @click="openReversePayment(payment)"
                      >
                        Reverse Payment
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="biz-empty-accounts">
                {{
                  billingAccountID
                    ? "No payments have been recorded for this employer yet."
                    : "A billing account is created when children are linked."
                }}
              </div>
            </div>
              </v-tab-item>

              <v-tab-item>
            <div class="biz-payments-section">
              <div class="biz-payments-title">
                <v-icon small color="#2e7d32" class="mr-2"
                  >mdi-calendar-clock</v-icon
                >
                Scheduled Payments
                <v-btn
                  v-if="pastDueAgentSchedules.length"
                  small
                  color="warning"
                  class="ma-0 ml-auto"
                  :loading="processingAgentPayments"
                  @click="openProcessAgentPayments(pastDueAgentSchedules)"
                >
                  Process {{ pastDueAgentSchedules.length }} past due
                </v-btn>
              </div>
              <table class="biz-students-table" v-if="schedules.length">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Next Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Processed</th>
                    <th>Card</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="schedule in schedules"
                    :key="scheduleKey(schedule)"
                    class="biz-clickable-row"
                    :class="{ 'biz-row-pastdue': schedule.needsProcessing }"
                    @click="openScheduleDetails(schedule)"
                  >
                    <td>
                      {{ formatPaymentDate(schedule.dateCreated) }}
                      <div v-if="rowNote(schedule)" class="biz-muted">
                        {{ rowNote(schedule) }}
                      </div>
                    </td>
                    <td>{{ formatDate(schedule.nextScheduleDate) || "-" }}</td>
                    <td>{{ formatCurrency(schedule.totalAmount) }}</td>
                    <td>
                      <span v-if="schedule.needsProcessing" class="biz-pastdue">
                        Past due · needs processing
                      </span>
                      <span v-else>{{ schedule.status }}</span>
                    </td>
                    <td>
                      {{ schedule.processed || 0 }}/{{
                        schedule.totalSchedules || 0
                      }}
                      <span v-if="schedule.error" class="biz-muted">
                        · {{ schedule.error }} error
                      </span>
                      <span v-if="schedule.declined" class="biz-muted">
                        · {{ schedule.declined }} declined
                      </span>
                    </td>
                    <td>
                      <span v-if="schedule.cardNumLast5">
                        {{ schedule.cardType }} · {{ schedule.cardNumLast5 }}
                      </span>
                      <span v-else class="biz-muted">{{
                        formatSchedulePaymentType(schedule.paymentType)
                      }}</span>
                    </td>
                    <td class="text-xs-right" @click.stop>
                      <v-btn
                        v-if="canProcessAgentPayment(schedule)"
                        small
                        dark
                        color="warning"
                        class="ma-0 mr-1"
                        :loading="processingAgentTxnId === schedule.txnID"
                        @click="openProcessAgentPayments([schedule])"
                      >
                        Process
                      </v-btn>
                      <v-btn
                        v-if="!schedule.isAgentPayment"
                        small
                        outline
                        color="success"
                        class="ma-0 mr-1"
                        @click="openScheduleDetails(schedule)"
                      >
                        View Details
                      </v-btn>
                      <v-btn
                        v-if="canCancelSchedule(schedule)"
                        small
                        dark
                        color="default"
                        class="ma-0"
                        @click="openCancelSchedule(schedule)"
                      >
                        Cancel
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="biz-empty-accounts">
                No scheduled payments for this employer yet.
              </div>
            </div>
              </v-tab-item>

              <v-tab-item>
            <div class="biz-ledger-card">
              <div class="biz-ledger-header">
                <div class="biz-ledger-title-group">
                  <v-icon small color="#2e7d32" class="mr-2"
                    >mdi-book-open-variant</v-icon
                  >
                  <span class="biz-ledger-title">Ledger</span>
                  <v-progress-circular
                    v-if="loadingLedger"
                    indeterminate
                    color="success"
                    size="16"
                    width="2"
                    class="ml-2"
                  ></v-progress-circular>
                </div>
              </div>

              <div
                v-if="!loadingLedger && !ledgerRows.length"
                class="biz-empty-accounts"
              >
                No monthly bills or payments yet for {{ term }}.
              </div>

              <table v-else-if="ledgerRows.length" class="biz-ledger-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th class="biz-ledger-amount">Amount</th>
                    <th class="biz-ledger-running">Running Balance</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in ledgerRows"
                    :key="row.txnID || row.date + '-' + row.type + '-' + idx"
                    :class="{ 'biz-row-pastdue': row.needsProcessing }"
                  >
                    <td class="biz-ledger-date">{{ formatDate(row.date) }}</td>
                    <td>
                      <div
                        class="biz-ledger-desc"
                        :class="{
                          'biz-ledger-desc--bill': row.type === 'Charge',
                          'biz-ledger-desc--payment': row.type === 'Payment',
                          'biz-ledger-desc--scheduled': row.type === 'Scheduled',
                          'biz-ledger-desc--pastdue': row.needsProcessing,
                        }"
                      >
                        {{ row.description }}
                      </div>
                      <div
                        v-if="row.detail"
                        class="biz-muted"
                        :class="{ 'biz-pastdue': row.needsProcessing }"
                      >
                        {{ row.detail }}
                      </div>
                    </td>
                    <td
                      class="biz-ledger-amount"
                      :class="{
                        'biz-ledger-amount--charge': row.type === 'Charge',
                        'biz-ledger-amount--payment': row.type === 'Payment',
                        'biz-ledger-amount--scheduled': row.type === 'Scheduled',
                        'biz-ledger-amount--pastdue': row.needsProcessing,
                      }"
                    >
                      {{ row.type === "Charge" ? "+" : "−"
                      }}{{ formatCurrency(row.amount) }}
                    </td>
                    <td class="biz-ledger-running">
                      {{ formatCurrency(row.balance) }}
                    </td>
                    <td class="text-xs-right">
                      <v-btn
                        v-if="row.needsProcessing && row.isAgentPayment"
                        small
                        dark
                        color="warning"
                        class="ma-0"
                        :loading="processingAgentTxnId === row.txnID"
                        @click="openProcessAgentPayments([row])"
                      >
                        Process
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
              </v-tab-item>
            </v-tabs-items>
          </div>

          <div v-else class="pa-5 text-xs-center">
            <v-progress-circular
              indeterminate
              color="success"
            ></v-progress-circular>
          </div>
        </material-card>
      </v-flex>
    </v-layout>

    <!-- ── Edit business dialog ── -->
    <v-dialog v-model="editDialog" max-width="600" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="success" class="mr-2">mdi-domain</v-icon>
          Edit Business
        </v-card-title>

        <v-text-field
          v-model="editForm.name"
          label="Business Name *"
          prepend-inner-icon="mdi-domain"
          outline
          dense
        ></v-text-field>
        <v-layout row wrap>
          <v-flex xs12 sm6 class="pr-2">
            <v-text-field
              v-model="editForm.contactPerson"
              label="Contact Person"
              prepend-inner-icon="mdi-account"
              outline
              dense
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 class="pl-2">
            <v-combobox
              v-model="editForm.contactPersonTitle"
              :items="contactPersonTitleOptions"
              label="Contact Title"
              prepend-inner-icon="mdi-account-card-details"
              outline
              dense
              hide-no-data
            ></v-combobox>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12 sm6 class="pr-2">
            <v-text-field
              v-model="editForm.phone"
              label="Phone"
              prepend-inner-icon="mdi-phone"
              outline
              dense
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6 class="pl-2">
            <v-text-field
              v-model="editForm.email"
              label="Email"
              prepend-inner-icon="mdi-email"
              outline
              dense
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-text-field
          v-model="editForm.address"
          label="Address"
          prepend-inner-icon="mdi-map-marker"
          outline
          dense
        ></v-text-field>
        <v-text-field
          v-model="editForm.ein"
          label="EIN"
          prepend-inner-icon="mdi-file-document-outline"
          outline
          dense
        ></v-text-field>
        <v-textarea
          v-model="editForm.note"
          label="Note"
          prepend-inner-icon="mdi-note-text"
          outline
          dense
          rows="2"
        ></v-textarea>

        <v-card-actions class="pr-0 pt-2 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="editDialog = false">Cancel</v-btn>
          <v-btn
            :loading="savingBusiness"
            color="success"
            @click="saveBusiness"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="removeDialog" max-width="440" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="error" class="mr-2">mdi-delete</v-icon>
          Delete Business
        </v-card-title>
        <div class="mb-3">
          Delete
          <span class="font-weight-medium">{{ business.name }}</span
          >? This can only be done when no families are linked.
        </div>
        <v-card-actions class="pr-0 pt-2 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="removeDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="removingBusiness"
            color="error"
            dark
            @click="removeBusiness"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Manage Agents dialog ── -->
    <v-dialog v-model="manageAgentsDialog" max-width="640" persistent scrollable>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="success" class="mr-2">mdi-account-tie</v-icon>
          Manage Billing Agents
        </v-card-title>
        <div class="caption grey--text mb-3">
          These are the entities that can help a business with the tax/tuition
          billing process. "UTA Direct" means UTA bills the employer directly.
        </div>

        <table class="biz-agents-table">
          <thead>
            <tr>
              <th>Name</th>
              <th style="width:110px">UTA Direct</th>
              <th style="width:90px">Active</th>
              <th style="width:80px">Order</th>
              <th style="width:70px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="agentRow in manageAgentsList" :key="agentRow.agentID || 'new'">
              <td>
                <v-text-field
                  v-model="agentRow.name"
                  hide-details
                  dense
                  placeholder="Agent name"
                ></v-text-field>
              </td>
              <td class="text-xs-center">
                <v-checkbox
                  v-model="agentRow.isUTADirect"
                  hide-details
                  class="mt-0 d-inline-block"
                ></v-checkbox>
              </td>
              <td class="text-xs-center">
                <v-checkbox
                  v-model="agentRow.active"
                  hide-details
                  class="mt-0 d-inline-block"
                ></v-checkbox>
              </td>
              <td>
                <v-text-field
                  v-model.number="agentRow.sortOrder"
                  type="number"
                  hide-details
                  dense
                ></v-text-field>
              </td>
              <td class="text-xs-right">
                <v-btn
                  small
                  color="success"
                  :loading="savingAgentRowId === (agentRow.agentID || 'new')"
                  @click="saveAgentRow(agentRow)"
                >
                  Save
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>

        <v-btn small outline color="success" class="mt-3" @click="addNewAgentRow">
          <v-icon left small>mdi-plus</v-icon>
          Add Agent
        </v-btn>

        <v-card-actions class="pr-0 pt-4 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeManageAgentsDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Change billing agent dialog ── -->
    <v-dialog v-model="changeAgentDialog" max-width="480" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="success" class="mr-2">mdi-account-tie</v-icon>
          Change Billing Agent
        </v-card-title>
        <div class="mb-3">
          Current agent:
          <span class="font-weight-medium">{{
            selectedAgentName || "Not set"
          }}</span>
          <span v-if="term" class="grey--text"> · {{ term }}</span>
        </div>
        <div class="caption grey--text mb-3">
          This agent is shown on the contract and invoices. Changing it does
          not affect what a parent requested on their application.
        </div>
        <v-select
          :items="changeAgentOptions"
          item-text="name"
          item-value="agentID"
          v-model="pendingAgentID"
          label="Billing Agent"
          outline
          dense
          hide-details
          clearable
        ></v-select>
        <v-card-actions class="pr-0 pt-4 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeChangeAgentDialog"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="savingAgent"
            :disabled="!agentChangePending"
            @click="confirmAgentChange"
          >
            Confirm Change
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Link / edit children dialog ── -->
    <v-dialog v-model="linkDialogVisible" max-width="800" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="success" class="mr-2">mdi-account-plus</v-icon>
          {{
            editingExistingFamily
              ? "Edit Covered Children"
              : "Link Family / Employee"
          }}
          — {{ term }}
        </v-card-title>

        <assign-account-lookup
          v-if="!editingExistingFamily"
          ref="accountLookup"
          search-url="api/businesses/searchApplicants"
          :extra-params="{ term: term, excludeBusinessId: businessId }"
          @selected="onAccountSelected"
        ></assign-account-lookup>
        <div v-if="!editingExistingFamily" class="assign-account-hint">
          Only accounts that applied for the Employer-Paid Child Care Program
          for {{ term }} show up here.
        </div>
        <div v-else class="biz-link-account-name mb-3">
          {{
            selectedAccount &&
              (selectedAccount.accountName || selectedAccount.accountID)
          }}
        </div>

        <div v-if="loadingLinkStudents" class="biz-empty-accounts">
          Loading children…
        </div>

        <div v-if="linkStudents.length" class="biz-link-students">
          <div class="biz-label mb-2">Select children to bill</div>
          <v-layout row wrap class="mb-2" align-end>
            <v-flex xs12 sm4 class="pr-2">
              <v-menu
                v-model="linkStartMenu"
                :close-on-content-click="false"
                offset-y
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="formatDate(linkCoverageStart)"
                    label="Default start date"
                    outline
                    dense
                    readonly
                    hide-details
                    prepend-inner-icon="mdi-calendar"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="linkCoverageStart"
                  :min="yearStart"
                  :max="yearEnd"
                  @input="linkStartMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12 sm4 class="pr-2">
              <v-menu
                v-model="linkEndMenu"
                :close-on-content-click="false"
                offset-y
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="formatDate(linkCoverageEnd)"
                    label="Default end date"
                    outline
                    dense
                    readonly
                    hide-details
                    prepend-inner-icon="mdi-calendar"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="linkCoverageEnd"
                  :min="yearStart"
                  :max="yearEnd"
                  @input="linkEndMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12 sm4>
              <v-btn
                outline
                color="success"
                class="mb-1"
                :disabled="!selectedLinkStudentIds.length"
                @click="applyLinkCoverageToSelected"
              >
                Apply to selected
              </v-btn>
            </v-flex>
          </v-layout>
          <div
            v-for="student in linkStudents"
            :key="student.studentID"
            class="biz-link-student-row"
          >
            <v-checkbox
              v-model="selectedLinkStudentIds"
              :value="normalizeStudentId(student.studentID)"
              :disabled="student.linkedToOtherBusiness"
              color="success"
              hide-details
              class="mt-0"
              @change="onLinkStudentToggled(student)"
            >
              <template slot="label">
                <span class="biz-td-name">{{
                  student.engName || student.hebName || student.studentID
                }}</span>
                <span v-if="student.class" class="biz-account-id">
                  {{ student.class }}
                </span>
                <span
                  v-if="student.linkedToOtherBusiness"
                  class="biz-account-id"
                >
                  already linked to another business
                </span>
              </template>
            </v-checkbox>
            <v-layout
              v-if="isLinkStudentSelected(student)"
              row
              wrap
              class="biz-link-student-dates"
            >
              <v-flex xs12 sm6 class="pr-2">
                <v-menu
                  v-model="student.startMenu"
                  :close-on-content-click="false"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="formatDate(student.coverageStartDate)"
                      label="Start"
                      outline
                      dense
                      readonly
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="student.coverageStartDate"
                    :min="yearStart"
                    :max="yearEnd"
                    @input="student.startMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-menu
                  v-model="student.endMenu"
                  :close-on-content-click="false"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="formatDate(student.coverageEndDate)"
                      label="End"
                      outline
                      dense
                      readonly
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="student.coverageEndDate"
                    :min="yearStart"
                    :max="yearEnd"
                    @input="student.endMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-flex>
            </v-layout>
          </div>
        </div>

        <v-card-actions class="pr-0 pt-3 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="linkDialogVisible = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="linking"
            :disabled="!canConfirmLink"
            color="success"
            @click="confirmLinkAccount"
          >
            <v-icon left small>mdi-link</v-icon>
            {{ editingExistingFamily ? "Save Children" : "Link Selected" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Remove signed contract confirm ── -->
    <v-dialog v-model="removeSignedContractDialog" max-width="480" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="error" class="mr-2">mdi-file-remove</v-icon>
          Remove Signed Contract
        </v-card-title>
        <v-card-text class="pl-0 pb-0">
          Remove the signed contract
          <strong v-if="signedContractFileName">{{
            signedContractFileName
          }}</strong>
          for <strong>{{ term }}</strong
          >? You can attach a new PDF afterward.
        </v-card-text>
        <v-card-actions class="pr-0 pt-3 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="removeSignedContractDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="removingSignedContract"
            color="error"
            @click="confirmRemoveSignedContract"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Unlink family confirm dialog ── -->
    <v-dialog v-model="unlinkConfirmVisible" max-width="500" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="error" class="mr-2">mdi-link-off</v-icon>
          Unlink Family
        </v-card-title>

        <v-card-text class="pl-0 pb-0">
          Unlink
          <strong>{{
            unlinkTarget && (unlinkTarget.accountName || unlinkTarget.accountID)
          }}</strong>
          from this business for <strong>{{ term }}</strong
          >? This will remove their childcare pricing from this employer's
          contract for the term.
        </v-card-text>

        <v-card-actions class="pr-0 pt-3 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="unlinkConfirmVisible = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="unlinkingId === (unlinkTarget && unlinkTarget.linkID)"
            color="error"
            @click="confirmUnlinkAccount"
          >
            <v-icon left small>mdi-link-off</v-icon>
            Unlink Family
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unlinkStudentConfirmVisible" max-width="480" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="error" class="mr-2">mdi-link-off</v-icon>
          Unlink Child
        </v-card-title>
        <div class="mb-3">
          Remove
          <span class="font-weight-medium">{{ unlinkStudentName }}</span>
          from this employer's bill for
          <span class="font-weight-medium">{{ term }}</span
          >?
        </div>
        <v-card-actions class="pr-0 pt-2 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn
            color="default"
            dark
            @click="unlinkStudentConfirmVisible = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            :loading="!!unlinkingStudentId"
            @click="confirmUnlinkStudent"
          >
            <v-icon left small>mdi-link-off</v-icon>
            Unlink
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Pay / schedule dialog ── -->
    <v-dialog persistent scrollable v-model="payDialogVisible" max-width="800">
      <make-payment
        v-if="payDialogVisible"
        ref="paymentDialog"
        :paymentMethods="paymentMethods"
        :closePaymentDialog="closePaymentDialog"
        :account="billingPaymentAccount"
        :refresh="loadDetail"
        :regID="term"
        source="ECC"
        categoryCode="ECC"
        categoryDescription="Employee Child Care"
      ></make-payment>
    </v-dialog>

    <v-dialog persistent v-model="reversePaymentDialog" max-width="400">
      <reverse-payment
        :closeDialog="closeReversePaymentDialog"
        :payment="paymentToReverse"
        :reloadPage="reloadAfterReverse"
        processURL="api/businesses/voidPayment"
      ></reverse-payment>
    </v-dialog>

    <v-dialog
      persistent
      scrollable
      v-model="paymentDetailsVisible"
      max-width="760"
    >
      <v-card v-if="paymentDetails">
        <v-card-title class="headline">
          <v-icon color="success" class="mr-2">mdi-file-document</v-icon>
          Payment Details
        </v-card-title>
        <v-card-text class="biz-dialog-body">
          <v-layout
            row
            class="py-1"
            v-for="prop in paymentDetailRows"
            :key="prop.name"
          >
            <div class="font-weight-medium mr-2" style="min-width: 180px;">
              {{ prop.name }}:
            </div>
            <div :class="{ 'red--text': prop.danger }">{{ prop.value }}</div>
          </v-layout>
          <div v-if="paymentCategoryRows.length" class="mt-3">
            <div class="font-weight-medium mb-1">Categories</div>
            <div
              v-for="(row, index) in paymentCategoryRows"
              :key="'cat-' + index"
            >
              {{ row }}
            </div>
          </div>
          <div v-if="paymentRefundRows.length" class="mt-3">
            <div class="font-weight-medium mb-1">Refunds</div>
            <div
              v-for="(row, index) in paymentRefundRows"
              :key="'ref-' + index"
              class="red--text"
            >
              {{ row }}
            </div>
          </div>
          <div v-if="paymentDetailsRelated.length" class="mt-4">
            <div class="font-weight-medium mb-2">
              Related schedule attempts / payments
            </div>
            <table class="biz-students-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="related in paymentDetailsRelated"
                  :key="related.paymentID"
                >
                  <td>{{ formatPaymentDate(related.date) }}</td>
                  <td>{{ formatCurrency(related.amount) }}</td>
                  <td>{{ related.statusValue || related.status }}</td>
                  <td>
                    {{ related.transactionResult || related.tranType || "-" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="paymentDetailsInstallments.length" class="mt-4">
            <div class="font-weight-medium mb-2">Schedule installments</div>
            <table class="biz-students-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Attempts</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in paymentDetailsInstallments"
                  :key="row.scheduleDetailID"
                >
                  <td>{{ formatDate(row.scheduleDate) }}</td>
                  <td>{{ formatCurrency(row.amount) }}</td>
                  <td>{{ row.status }}</td>
                  <td>{{ row.attempts || "-" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="paymentDetails.scheduleID"
            outline
            color="success"
            @click="openScheduleFromPayment(paymentDetails)"
          >
            View Schedule
          </v-btn>
          <v-btn
            v-if="canReverse(paymentDetails)"
            dark
            color="default"
            @click="openReverseFromDetails"
          >
            Reverse Payment
          </v-btn>
          <v-btn color="default" dark @click="paymentDetailsVisible = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      persistent
      scrollable
      v-model="scheduleDialogVisible"
      max-width="980"
    >
      <v-card v-if="selectedSchedule">
        <v-card-title class="headline">
          <v-icon color="success" class="mr-2">mdi-calendar-clock</v-icon>
          Schedule Details
          <v-spacer></v-spacer>
          <span class="biz-schedule-pill">{{ selectedSchedule.status }}</span>
        </v-card-title>
        <v-card-text class="biz-dialog-body">
          <div class="biz-schedule-subtitle">
            {{ selectedSchedule.name }}
            <span v-if="selectedSchedule.accountID">
              · #{{ selectedSchedule.accountID }}
            </span>
          </div>
          <div class="biz-schedule-grid">
            <div class="biz-schedule-field">
              <div class="biz-schedule-label">Created</div>
              <div>{{ formatPaymentDate(selectedSchedule.dateCreated) }}</div>
            </div>
            <div class="biz-schedule-field">
              <div class="biz-schedule-label">Amount</div>
              <div>{{ formatCurrency(selectedSchedule.totalAmount) }}</div>
            </div>
            <div class="biz-schedule-field">
              <div class="biz-schedule-label">Start / End</div>
              <div>
                {{ formatDate(selectedSchedule.startDate) }} –
                {{ formatDate(selectedSchedule.endDate) }}
              </div>
            </div>
            <div class="biz-schedule-field">
              <div class="biz-schedule-label">Processed</div>
              <div>
                {{ formatCurrency(selectedSchedule.amountProcessed) }}
                · {{ selectedSchedule.processed || 0 }}/{{
                  selectedSchedule.totalSchedules || 0
                }}
              </div>
            </div>
            <div class="biz-schedule-field">
              <div class="biz-schedule-label">Next Date</div>
              <div>
                {{ formatDate(selectedSchedule.nextScheduleDate) || "-" }}
              </div>
            </div>
            <div class="biz-schedule-field">
              <div class="biz-schedule-label">Method</div>
              <div>{{ formatScheduleMethod(selectedSchedule) }}</div>
            </div>
            <div
              class="biz-schedule-field"
              v-if="selectedSchedule.lastSuccessfulSchedule"
            >
              <div class="biz-schedule-label">Last Successful</div>
              <div>
                {{ formatDate(selectedSchedule.lastSuccessfulSchedule) }}
              </div>
            </div>
            <div
              class="biz-schedule-field"
              v-if="selectedSchedule.emailAddress"
            >
              <div class="biz-schedule-label">Email</div>
              <div>{{ selectedSchedule.emailAddress }}</div>
            </div>
            <div class="biz-schedule-field" v-if="selectedSchedule.userName">
              <div class="biz-schedule-label">Entered By</div>
              <div>{{ selectedSchedule.userName }}</div>
            </div>
            <div class="biz-schedule-field" v-if="selectedSchedule.comments">
              <div class="biz-schedule-label">Comments</div>
              <div>{{ selectedSchedule.comments }}</div>
            </div>
          </div>
          <v-tabs
            v-model="scheduleTab"
            color="transparent"
            slider-color="success"
            class="biz-schedule-tabs"
          >
            <v-tab>Installments</v-tab>
            <v-tab>
              Payments
              <span
                class="biz-tab-count"
                :class="{
                  'biz-tab-count-alert': scheduleRelatedPayments.length,
                }"
                >{{ scheduleRelatedPayments.length }}</span
              >
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="scheduleTab">
            <v-tab-item>
              <schedule-details
                v-if="scheduleDialogVisible && selectedSchedule"
                :row="selectedSchedule"
                :columns="emptyScheduleColumns"
                :reloadGrid="reloadAfterScheduleChange"
                :tableOnly="true"
              ></schedule-details>
            </v-tab-item>
            <v-tab-item>
              <div class="mt-3">
                <table
                  class="biz-students-table"
                  v-if="scheduleRelatedPayments.length"
                >
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Result</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="related in scheduleRelatedPayments"
                      :key="related.paymentID"
                      class="biz-clickable-row"
                      @click="openPaymentFromSchedule(related)"
                    >
                      <td>{{ formatPaymentDate(related.date) }}</td>
                      <td>{{ formatCurrency(related.amount) }}</td>
                      <td>{{ related.statusValue || related.status }}</td>
                      <td>
                        {{
                          related.transactionResult || related.tranType || "-"
                        }}
                      </td>
                      <td class="text-xs-right" @click.stop>
                        <v-btn
                          small
                          outline
                          color="success"
                          class="ma-0"
                          @click="openPaymentFromSchedule(related)"
                        >
                          View Details
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="biz-muted">
                  No payment attempts have been recorded for this schedule yet.
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="canCancelSchedule(selectedSchedule)"
            dark
            color="error"
            @click="openCancelSchedule(selectedSchedule)"
          >
            Cancel Schedule
          </v-btn>
          <v-btn color="default" dark @click="scheduleDialogVisible = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelScheduleDialog" max-width="440" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">{{
          cancelScheduleTitle
        }}</v-card-title>

        <template v-if="isCancelingAgentPayment && scheduleToCancel">
          <div class="biz-cancel-summary">
            <div class="biz-cancel-summary-label">Selected payment</div>
            <div class="biz-cancel-summary-row">
              <span>{{
                formatDate(
                  scheduleToCancel.nextScheduleDate || scheduleToCancel.date
                )
              }}</span>
              <span class="biz-cancel-summary-amount">{{
                formatCurrency(
                  scheduleToCancel.totalAmount || scheduleToCancel.amount
                )
              }}</span>
            </div>
            <div v-if="rowNote(scheduleToCancel)" class="biz-muted mt-1">
              {{ rowNote(scheduleToCancel) }}
            </div>
          </div>

          <div class="biz-cancel-choices">
            <v-btn
              v-if="relatedAgentSchedules.length > 1"
              block
              outline
              color="error"
              class="ma-0 mb-2"
              :loading="cancellingSchedule"
              @click="confirmCancelSchedule(false)"
            >
              Cancel this payment only
            </v-btn>
            <v-btn
              v-if="relatedAgentSchedules.length > 1"
              block
              color="error"
              class="ma-0"
              :loading="cancellingSchedule"
              @click="confirmCancelSchedule(true)"
            >
              Cancel all {{ relatedAgentSchedules.length }} remaining
            </v-btn>
            <div
              v-if="relatedAgentSchedules.length > 1"
              class="biz-muted biz-cancel-hint"
            >
              This removes every unprocessed Agent Payment from this schedule.
            </div>
          </div>
        </template>

        <div v-else class="mb-3">
          {{ cancelScheduleText }}
        </div>

        <v-card-actions class="pr-0 pt-3 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelScheduleDialog = false"
            >Keep</v-btn
          >
          <v-btn
            v-if="
              !isCancelingAgentPayment || relatedAgentSchedules.length <= 1
            "
            :loading="cancellingSchedule"
            color="error"
            @click="confirmCancelSchedule(false)"
          >
            Cancel Schedule
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="processAgentDialog" max-width="480" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          Process Agent Payment
        </v-card-title>
        <div class="mb-3">
          {{ processAgentDialogText }}
        </div>
        <v-card-actions class="pr-0 pt-2 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="processAgentDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="processingAgentPayments"
            color="success"
            @click="confirmProcessAgentPayments"
          >
            Process
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Coverage dates dialog ── -->
    <v-dialog v-model="coverageDialogVisible" max-width="520" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          <v-icon color="success" class="mr-2">mdi-calendar-edit</v-icon>
          Adjust Coverage
        </v-card-title>
        <div class="biz-rate-dialog-account mb-3">
          {{ coverageForm.name }}
        </div>
        <div class="caption grey--text mb-3">
          Billed by month, with no daily proration. A start or end in the middle
          of a month still covers that month.
        </div>
        <v-alert
          :value="!!coverageError"
          type="error"
          @input="coverageError = ''"
        >
          {{ coverageError }}
        </v-alert>
        <v-layout row wrap>
          <v-flex xs12 sm6 class="pr-2">
            <v-menu
              v-model="coverageStartMenu"
              :close-on-content-click="false"
              offset-y
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="formatDate(coverageForm.startDate)"
                  label="Start date"
                  outline
                  dense
                  readonly
                  prepend-inner-icon="mdi-calendar"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="coverageForm.startDate"
                :min="yearStart"
                :max="yearEnd"
                @input="coverageStartMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex xs12 sm6 class="pl-2">
            <v-menu
              v-model="coverageEndMenu"
              :close-on-content-click="false"
              offset-y
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="formatDate(coverageForm.endDate)"
                  label="End date"
                  outline
                  dense
                  readonly
                  prepend-inner-icon="mdi-calendar"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="coverageForm.endDate"
                :min="yearStart"
                :max="yearEnd"
                @input="coverageEndMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-flex>
        </v-layout>
        <v-card-actions class="pr-0 pt-2 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="coverageDialogVisible = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="savingCoverage"
            color="success"
            @click="saveCoverageDialog"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Edit rates dialog ── -->
    <v-dialog v-model="rateDialogVisible" max-width="780" persistent scrollable>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="success" class="mr-2">mdi-pencil</v-icon>
          Edit Rates
          <span v-if="rateDialogAccount" class="biz-rate-dialog-account">
            — {{ rateDialogAccount.accountName || rateDialogAccount.accountID }}
          </span>
        </v-card-title>

        <v-card-text class="biz-dialog-body">
          <v-alert :value="!!rateError" type="error" @input="rateError = ''">
            {{ rateError }}
          </v-alert>

          <div class="biz-rate-bulk">
            <div class="biz-label mb-2">Apply to all students</div>
            <v-layout row wrap align-center>
              <v-flex xs12 sm5 class="pr-2">
                <v-select
                  v-model="bulkRateType"
                  :items="bulkRateTypeOptions"
                  label="Rate Type"
                  outline
                  dense
                  hide-details
                  clearable
                ></v-select>
              </v-flex>
              <v-flex xs12 sm4 class="pr-2" v-if="bulkRateType === 'Custom'">
                <v-text-field
                  v-model.number="bulkMonthlyRate"
                  label="Custom Monthly Rate ($ / mo)"
                  type="number"
                  outline
                  dense
                  hide-details
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm3>
                <v-btn
                  outline
                  color="success"
                  class="mt-1"
                  :disabled="!bulkRateType"
                  @click="applyRateToAll"
                >
                  Apply to All
                </v-btn>
              </v-flex>
            </v-layout>
          </div>

          <div class="biz-rate-list">
            <div
              v-for="row in rateForm"
              :key="row.studentID"
              class="biz-rate-row"
            >
              <div class="biz-rate-row-name">{{ row.name }}</div>
              <div class="biz-rate-fields">
                <div>
                  <v-select
                    v-model="row.rateType"
                    :items="rateTypeOptionsForRow(row)"
                    label="Rate Type"
                    outline
                    dense
                    hide-details
                    @change="onRowRateTypeChanged(row)"
                  ></v-select>
                </div>
                <div v-if="row.rateType === 'Custom'">
                  <v-text-field
                    v-model.number="row.customMonthlyRate"
                    label="Custom Monthly Rate ($ / mo)"
                    type="number"
                    outline
                    dense
                    hide-details
                  ></v-text-field>
                </div>
                <div>
                  <v-menu
                    v-model="row.startMenu"
                    :close-on-content-click="false"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formatDate(row.coverageStartDate)"
                        label="Coverage start"
                        outline
                        dense
                        readonly
                        hide-details
                        prepend-inner-icon="mdi-calendar"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="row.coverageStartDate"
                      :min="yearStart"
                      :max="yearEnd"
                      @input="row.startMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </div>
                <div>
                  <v-menu
                    v-model="row.endMenu"
                    :close-on-content-click="false"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formatDate(row.coverageEndDate)"
                        label="Coverage end"
                        outline
                        dense
                        readonly
                        hide-details
                        prepend-inner-icon="mdi-calendar"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="row.coverageEndDate"
                      :min="yearStart"
                      :max="yearEnd"
                      @input="row.endMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </div>
              </div>
            </div>

            <div v-if="!rateForm.length" class="biz-empty-accounts">
              No covered children found for this family/term.
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="rateDialogVisible = false"
            >Cancel</v-btn
          >
          <v-btn
            :loading="savingRates"
            :disabled="!rateForm.length"
            color="success"
            @click="saveRates"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import AssignAccountLookup from "../components/AssignAccountLookup.vue";
import reversePayment from "../Account/ReversePayment.vue";
import makePayment from "../Payment/MakePayment.vue";
import scheduleDetails from "../components/ScheduleDetails.vue";
import {
  formatCurrency as formatCurrencyUtil,
  formatPhoneNumber,
  except,
  toTitleCase,
} from "../../utils/helpers.js";

export default {
  name: "BusinessView",
  components: {
    AssignAccountLookup,
    reversePayment,
    makePayment,
    scheduleDetails,
  },
  data: () => ({
    businessId: 0,
    loading: true,
    business: {},
    accounts: [],
    annualTotal: 0,
    monthlyTotal: 0,
    balance: 0,
    billingAccountID: "",
    hasLinkedAccounts: false,
    yearStart: "",
    yearEnd: "",
    term: "",
    currentTerm: "",
    terms: [],
    agents: [],
    selectedAgentID: null,
    pendingAgentID: null,
    savingAgent: false,
    changeAgentDialog: false,
    businessTab: 0,
    manageAgentsDialog: false,
    manageAgentsList: [],
    savingAgentRowId: null,
    ledgerMonths: [],
    loadingLedger: false,
    rateTypeOptions: [
      { text: "List Price", value: "List" },
      { text: "Price A", value: "PriceA" },
      { text: "Custom Rate", value: "Custom" },
    ],
    bulkRateTypeOptions: [
      { text: "List Price (Default)", value: "List" },
      { text: "Price A", value: "PriceA" },
      { text: "Custom Rate", value: "Custom" },
    ],
    contactPersonTitleOptions: [
      "Owner",
      "President",
      "CEO",
      "CFO",
      "Controller",
      "Office Manager",
      "HR Manager",
      "Payroll Manager",
      "Bookkeeper",
      "Accountant",
      "Manager",
    ],
    editDialog: false,
    editForm: {},
    savingBusiness: false,
    removeDialog: false,
    removingBusiness: false,
    linkDialogVisible: false,
    selectedAccount: null,
    linking: false,
    editingExistingFamily: false,
    linkStudents: [],
    selectedLinkStudentIds: [],
    loadingLinkStudents: false,
    linkCoverageStart: "",
    linkCoverageEnd: "",
    linkStartMenu: false,
    linkEndMenu: false,
    unlinkingId: null,
    unlinkingStudentId: null,
    unlinkConfirmVisible: false,
    unlinkTarget: null,
    unlinkStudentConfirmVisible: false,
    unlinkStudentTarget: null,
    coverageDialogVisible: false,
    savingCoverage: false,
    coverageError: "",
    coverageStartMenu: false,
    coverageEndMenu: false,
    coverageForm: {
      account: null,
      student: null,
      name: "",
      startDate: "",
      endDate: "",
    },
    rateDialogVisible: false,
    rateDialogAccount: null,
    rateForm: [],
    rateError: "",
    bulkRateType: null,
    bulkMonthlyRate: null,
    savingRates: false,
    downloadingContract: false,
    downloadingInvoices: false,
    hasSignedContract: false,
    signedContractFileName: "",
    signedContractUploadedAt: null,
    signedContractUploadedBy: "",
    uploadingSignedContract: false,
    openingSignedContract: false,
    removingSignedContract: false,
    removeSignedContractDialog: false,
    payments: [],
    schedules: [],
    paymentDetailsVisible: false,
    paymentDetails: null,
    paymentDetailsRelated: [],
    paymentDetailsInstallments: [],
    scheduleDialogVisible: false,
    selectedSchedule: null,
    scheduleRelatedPayments: [],
    scheduleTab: 0,
    emptyScheduleColumns: [],
    cancelScheduleDialog: false,
    scheduleToCancel: null,
    cancellingSchedule: false,
    processAgentDialog: false,
    processAgentTargets: [],
    processingAgentPayments: false,
    processingAgentTxnId: null,
    reversePaymentDialog: false,
    paymentToReverse: {
      details: [],
    },
    payDialogVisible: false,
    paymentMethods: [],
  }),
  computed: {
    studentCount() {
      return this.accounts.reduce((sum, a) => sum + a.students.length, 0);
    },
    publishedAnnualCharges() {
      return (this.accounts || []).reduce((sum, account) => {
        return (
          sum +
          (account.students || []).reduce((inner, student) => {
            const months =
              Number(student.monthsCovered) > 0
                ? Number(student.monthsCovered)
                : 12;
            return inner + ((Number(student.listAnnual) || 0) * months) / 12;
          }, 0)
        );
      }, 0);
    },
    contractDiscount() {
      return Math.max(
        this.publishedAnnualCharges - (Number(this.annualTotal) || 0),
        0
      );
    },
    totalPaid() {
      return (this.payments || [])
        .filter((p) => p && p.status === "PROCESSED")
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    },
    totalScheduled() {
      // Base the "still to come" amount on the number of installments that
      // are actually still pending (schedule.scheduled), not just on
      // totalAmount - amountProcessed. A fully-canceled schedule ends up
      // with status "Completed" (nothing left to run) but amountProcessed
      // is still 0, so subtracting alone would wrongly count the whole
      // contract as scheduled even though every installment was canceled.
      return (this.schedules || [])
        .filter((s) => s && s.isEnabled !== false)
        .reduce((sum, s) => {
          const totalCount = Number(s.totalSchedules) || 0;
          const pendingCount = Number(s.scheduled) || 0;
          if (totalCount <= 0 || pendingCount <= 0) return sum;
          const perInstallment = (Number(s.totalAmount) || 0) / totalCount;
          return sum + Math.max(perInstallment * pendingCount, 0);
        }, 0);
    },
    canConfirmLink() {
      return (
        this.selectedAccount &&
        this.selectedLinkStudentIds &&
        this.selectedLinkStudentIds.length > 0
      );
    },
    unlinkStudentName() {
      const student =
        this.unlinkStudentTarget && this.unlinkStudentTarget.student;
      if (!student) return "this child";
      return student.engName || student.hebName || student.studentID;
    },
    billingPaymentAccount() {
      return {
        accountID: this.billingAccountID,
        name: (this.business && this.business.name) || "",
      };
    },
    selectedAgentName() {
      const agent = (this.agents || []).find(
        (a) => a.agentID === this.selectedAgentID
      );
      return agent ? agent.name : "";
    },
    changeAgentOptions() {
      return (this.agents || []).filter(
        (a) => a.active || a.agentID === this.selectedAgentID
      );
    },
    agentChangePending() {
      return this.pendingAgentID !== this.selectedAgentID;
    },
    ledgerRows() {
      return (this.ledgerMonths || []).reduce((rows, monthGroup) => {
        return rows.concat(monthGroup.rows || []);
      }, []);
    },
    pastDueAgentSchedules() {
      return (this.schedules || []).filter(
        (s) => s && s.isAgentPayment && s.needsProcessing
      );
    },
    processAgentDialogText() {
      const rows = this.processAgentTargets || [];
      if (rows.length === 1) {
        const row = rows[0];
        const amount = this.formatCurrency(row.totalAmount || row.amount);
        const date = this.formatDate(row.nextScheduleDate || row.date);
        return (
          "Record this Agent Payment as processed" +
          (date ? " for " + date : "") +
          (amount ? " (" + amount + ")" : "") +
          "? It will be applied to the employer balance and assigned a receipt number."
        );
      }
      return (
        "Record " +
        rows.length +
        " past-due Agent Payments as processed? They will be applied to the employer balance and assigned receipt numbers."
      );
    },
    isCancelingAgentPayment() {
      return !!(this.scheduleToCancel && this.scheduleToCancel.isAgentPayment);
    },
    relatedAgentSchedules() {
      const schedule = this.scheduleToCancel;
      if (!schedule || !schedule.isAgentPayment) return [];

      const created = schedule.dateCreated
        ? moment(schedule.dateCreated).format("YYYY-MM-DD HH:mm")
        : "";

      return (this.schedules || []).filter((row) => {
        if (!row || !row.isAgentPayment) return false;
        if (!created) return row.txnID === schedule.txnID;
        return (
          moment(row.dateCreated).format("YYYY-MM-DD HH:mm") === created
        );
      });
    },
    cancelScheduleTitle() {
      if (!this.isCancelingAgentPayment) return "Cancel Schedule";
      return this.relatedAgentSchedules.length > 1
        ? "Cancel Agent Payment"
        : "Cancel Schedule";
    },
    cancelScheduleText() {
      if (!this.isCancelingAgentPayment) {
        return "This will cancel remaining scheduled payments on this card/ACH schedule.";
      }

      const schedule = this.scheduleToCancel;
      const amount = this.formatCurrency(
        schedule.totalAmount || schedule.amount
      );
      const date = this.formatDate(schedule.nextScheduleDate || schedule.date);
      const batchCount = this.relatedAgentSchedules.length;

      if (batchCount > 1) {
        return (
          "Cancel the " +
          date +
          " payment of " +
          amount +
          ", or cancel all " +
          batchCount +
          " remaining Agent Payments from this schedule?"
        );
      }

      return "Cancel the " + date + " Agent Payment of " + amount + "?";
    },
    paymentDetailRows() {
      if (!this.paymentDetails) return [];

      const payment = this.paymentDetails;
      const skip = ["details", "refunds", "categories", "scheduleDetails"];
      const dateKeys = ["date"];

      return Object.keys(except(payment, ...skip))
        .map((key) => {
          let value = payment[key];
          if (value == null || value === "") return null;
          if (typeof value === "boolean") value = value ? "Yes" : "No";
          if (key === "amount") value = this.formatCurrency(value);
          if (key === "paymentType") {
            value = this.formatSchedulePaymentType(value);
          }
          if (dateKeys.includes(key) || /date/i.test(key)) {
            const formatted = this.formatPaymentDate(value);
            if (formatted) value = formatted;
          }
          return {
            name: toTitleCase(key),
            value: String(value),
            danger: key === "transactionResult" && payment.status === "ERROR",
          };
        })
        .filter(Boolean);
    },
    paymentCategoryRows() {
      const details = this.paymentDetails && this.paymentDetails.details;
      if (!details) return [];

      return String(details)
        .split("|")
        .map((detail) => {
          const parts = String(detail).split(":");
          const code = (parts[0] || "").trim();
          const amount = parts[1];
          if (!code) return "";
          const name = code === "ECC" ? "Employee Child Care" : code;
          return amount ? name + ": " + this.formatCurrency(amount) : name;
        })
        .filter(Boolean);
    },
    paymentRefundRows() {
      const refunds = this.paymentDetails && this.paymentDetails.refunds;
      if (!refunds) return [];

      return String(refunds)
        .split("|")
        .map((detail) => {
          const parts = String(detail).split(",");
          const date = this.formatPaymentDate(parts[0]);
          const amount = parts[1];
          return (date || parts[0] || "") + ": " + this.formatCurrency(amount);
        })
        .filter(Boolean);
    },
  },
  watch: {
    "$route.params.term"(newTerm) {
      // Only fires when navigating between years for the same business
      // (id unchanged); a different business id remounts the component.
      const resolved = newTerm || this.currentTerm || "";

      if (resolved === this.term) return;

      this.term = resolved;
      this.loadDetail();
    },
  },
  async created() {
    this.businessId = parseInt(this.$route.params.id, 10);

    this.currentTerm = await this.loadCurrentTerm();
    this.term = this.$route.params.term || this.currentTerm;

    await this.loadTerms();
    await Promise.all([this.loadAgents(), this.loadDetail()]);
  },
  methods: {
    navigateToTerm(term) {
      if (!term || term === this.term) return;

      this.$router.push({
        name: "BusinessView",
        params: { id: this.businessId, term },
      });
    },
    formatCurrency(value) {
      return formatCurrencyUtil(value || 0);
    },
    formatPhone(value) {
      return value ? formatPhoneNumber(value) || value : "";
    },
    formatDate(value) {
      if (!value) return "";
      const m = moment(value);
      return m.isValid() ? m.format("MM/DD/YYYY") : "";
    },
    signedContractTooltipFor() {
      const parts = [this.signedContractFileName || "Signed contract"];
      if (this.signedContractUploadedAt) {
        parts.push(
          "Uploaded " +
            this.formatDateTime(this.signedContractUploadedAt) +
            (this.signedContractUploadedBy
              ? " by " + this.signedContractUploadedBy
              : "")
        );
      }
      return parts.join("\n");
    },
    formatDateTime(value) {
      if (!value) return "";
      const m = moment(value);
      return m.isValid() ? m.format("MM/DD/YYYY h:mm A") : "";
    },
    formatMonth(value) {
      if (!value) return "";
      const m = moment(value);
      return m.isValid() ? m.format("MMMM YYYY") : "";
    },
    accountInvoiceTotal(account) {
      return (account.invoices || []).reduce(
        (sum, invoice) => sum + (Number(invoice.total) || 0),
        0
      );
    },
    toDateInput(value) {
      if (!value) return "";
      const m = moment(value);
      return m.isValid() ? m.format("YYYY-MM-DD") : "";
    },
    datesEqual(left, right) {
      return this.toDateInput(left) === this.toDateInput(right);
    },
    coverageRangeError(startDate, endDate) {
      if (!startDate || !endDate) {
        return "Start and end dates are required.";
      }
      if (moment(endDate).isBefore(moment(startDate), "day")) {
        return "Coverage end date must be on or after the start date.";
      }
      return "";
    },
    isLinkStudentSelected(student) {
      return this.selectedLinkStudentIds.includes(
        this.normalizeStudentId(student.studentID)
      );
    },
    rateTypeLabel(student) {
      const type =
        student && typeof student === "object" ? student.rateType : student;
      const match = this.rateTypeOptions.find((opt) => opt.value === type);
      const name = (match && match.text) || type || "—";
      if (!student || typeof student !== "object" || type === "Custom") {
        return name;
      }

      const annual =
        type === "List" ? student.listAnnual : student.priceAAnnual;
      if (annual == null || annual === "") return name;

      return (
        name + " · " + this.formatCurrency(this.annualToMonthly(annual)) + "/mo"
      );
    },
    rateTypeOptionsFor(student) {
      const listMo = student ? this.annualToMonthly(student.listAnnual) : null;
      const priceAMo = student
        ? this.annualToMonthly(student.priceAAnnual)
        : null;

      return [
        {
          text:
            listMo != null
              ? "List Price (Default) — " + this.formatCurrency(listMo) + "/mo"
              : "List Price (Default)",
          value: "List",
        },
        {
          text:
            priceAMo != null
              ? "Price A — " + this.formatCurrency(priceAMo) + "/mo"
              : "Price A",
          value: "PriceA",
        },
        { text: "Custom Rate", value: "Custom" },
      ];
    },
    rateTypeOptionsForRow(row) {
      return this.rateTypeOptionsFor(
        this.findStudent(this.rateDialogAccount, row.studentID)
      );
    },
    monthlyToAnnual(monthly) {
      return Math.round((Number(monthly) || 0) * 12 * 100) / 100;
    },
    annualToMonthly(annual) {
      if (annual == null || annual === "") return null;
      return Math.round((Number(annual) / 12) * 100) / 100;
    },
    normalizeStudentId(id) {
      return id == null ? "" : String(id).trim();
    },
    findStudent(account, studentId) {
      const key = this.normalizeStudentId(studentId);
      if (!account || !account.students) return null;
      return account.students.find(
        (s) => this.normalizeStudentId(s.studentID) === key
      );
    },
    async loadCurrentTerm() {
      try {
        return await this.$http.get("api/businesses/getCurrentTerm");
      } catch (e) {
        return "RG2027";
      }
    },
    async loadTerms() {
      try {
        const response = await this.$http.get(
          "api/businesses/getTerms?businessId=" + this.businessId
        );
        const list = Array.isArray(response) ? response : [];

        if (this.term && !list.includes(this.term)) {
          list.unshift(this.term);
        }

        this.terms = list;
      } catch (e) {
        this.terms = this.term ? [this.term] : [];
      }
    },
    async loadDetail() {
      this.loading = true;

      try {
        const response = await this.$http.get(
          "api/businesses/getDetail?businessId=" +
            this.businessId +
            "&term=" +
            encodeURIComponent(this.term)
        );

        this.business = response.business || {};
        this.accounts = response.accounts || [];
        this.annualTotal = response.annualTotal || 0;
        this.monthlyTotal = response.monthlyTotal || 0;
        this.balance = response.balance || 0;
        this.billingAccountID = response.billingAccountID || "";
        this.hasLinkedAccounts = !!response.hasLinkedAccounts;
        this.yearStart = this.toDateInput(response.yearStart);
        this.yearEnd = this.toDateInput(response.yearEnd);
        this.selectedAgentID = response.agentID || null;
        this.setSignedContract(
          response.hasSignedContract,
          response.signedContractFileName,
          response.signedContractUploadedAt,
          response.signedContractUploadedBy
        );
        await Promise.all([this.loadPayments(), this.loadLedger()]);
      } catch (e) {
        this.accounts = [];
        this.payments = [];
        this.schedules = [];
        this.setSignedContract(false);
      } finally {
        this.loading = false;
      }
    },
    async loadAgents() {
      try {
        const response = await this.$http.get(
          "api/businesses/getAgents?includeInactive=true"
        );
        this.agents = Array.isArray(response) ? response : [];
      } catch (e) {
        this.agents = [];
      }
    },
    openChangeAgentDialog() {
      this.pendingAgentID = this.selectedAgentID;
      this.changeAgentDialog = true;
    },
    closeChangeAgentDialog() {
      this.changeAgentDialog = false;
      this.pendingAgentID = this.selectedAgentID;
    },
    async confirmAgentChange() {
      if (!this.businessId || !this.agentChangePending) return;

      try {
        this.savingAgent = true;

        await this.$http.post("api/businesses/saveAgent", {
          businessID: this.businessId,
          term: this.term,
          agentID: this.pendingAgentID || null,
        });

        this.selectedAgentID = this.pendingAgentID || null;
        this.changeAgentDialog = false;

        this.$notify({
          text: "Billing agent updated",
          group: "notification",
          type: "success",
        });
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to update billing agent"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.savingAgent = false;
      }
    },
    openManageAgentsDialog() {
      this.manageAgentsList = (this.agents || []).map((a) =>
        Object.assign({}, a)
      );
      this.manageAgentsDialog = true;
    },
    closeManageAgentsDialog() {
      this.manageAgentsDialog = false;
    },
    addNewAgentRow() {
      this.manageAgentsList.push({
        agentID: 0,
        name: "",
        isUTADirect: false,
        active: true,
        sortOrder: this.manageAgentsList.length + 1,
      });
    },
    async saveAgentRow(agentRow) {
      if (!agentRow.name || !agentRow.name.trim()) {
        this.$notify({
          text: "Agent name is required",
          group: "notification",
          type: "error",
        });
        return;
      }

      const rowId = agentRow.agentID || "new";

      try {
        this.savingAgentRowId = rowId;

        await this.$http.post("api/businesses/saveAgentRecord", {
          agentID: agentRow.agentID || 0,
          name: agentRow.name.trim(),
          isUTADirect: !!agentRow.isUTADirect,
          active: !!agentRow.active,
          sortOrder: agentRow.sortOrder || null,
        });

        this.$notify({
          text: "Agent saved",
          group: "notification",
          type: "success",
        });

        await this.loadAgents();
        this.manageAgentsList = (this.agents || []).map((a) =>
          Object.assign({}, a)
        );
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to save agent"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.savingAgentRowId = null;
      }
    },
    async loadLedger() {
      if (!this.businessId) {
        this.ledgerMonths = [];
        return;
      }

      this.loadingLedger = true;

      try {
        const response = await this.$http.get(
          "api/businesses/getLedger?businessId=" + this.businessId
        );
        this.ledgerMonths = Array.isArray(response.months)
          ? response.months
          : [];
      } catch (e) {
        this.ledgerMonths = [];
      } finally {
        this.loadingLedger = false;
      }
    },
    formatLedgerMonth(month) {
      return month ? moment(month).format("MMMM YYYY") : "";
    },
    async loadPayments() {
      if (!this.businessId) {
        this.payments = [];
        this.schedules = [];
        return;
      }

      try {
        const [payments, schedules] = await Promise.all([
          this.$http.get(
            "api/businesses/getPayments?businessId=" + this.businessId
          ),
          this.$http.get(
            "api/businesses/getSchedules?businessId=" + this.businessId
          ),
        ]);
        this.payments = Array.isArray(payments) ? payments : [];
        this.schedules = Array.isArray(schedules) ? schedules : [];
      } catch (e) {
        this.payments = [];
        this.schedules = [];
      }
    },
    formatPaymentDate(date) {
      return date ? moment(date).format("MM/DD/YYYY h:mm A") : "";
    },
    rowNote(row) {
      if (!row) return "";
      const note = (row.comments || row.note || "").toString().trim();
      return note;
    },
    formatSchedulePaymentType(paymentType) {
      const type = (paymentType || "").toString();
      const upper = type.toUpperCase();
      if (type === "Agent Payment") return "Agent Payment";
      if (upper === "CK" || upper.includes("CHECK") || upper.includes("CASH")) {
        return "Cash/Check";
      }
      if (upper === "CC" || upper.includes("CREDIT")) {
        return "Credit Card";
      }
      if (upper === "ACH") return "ACH";
      return paymentType || "-";
    },
    formatScheduleMethod(schedule) {
      if (!schedule) return "-";
      const typeLabel = this.formatSchedulePaymentType(schedule.paymentType);
      const last5 = schedule.cardNumLast5;
      const cardType = schedule.cardType;
      if (cardType && last5) return cardType + " · " + last5;
      if (last5) return typeLabel + " · " + last5;
      return typeLabel;
    },
    canReverse(payment) {
      return (
        payment &&
        !payment.isLedgerPayment &&
        payment.status == "PROCESSED" &&
        (payment.tranType == "Sale" || !payment.tranType) &&
        !payment.isVoided
      );
    },
    canCancelSchedule(schedule) {
      if (schedule && schedule.isAgentPayment) {
        return schedule.status == "Scheduled" || schedule.needsProcessing;
      }

      return (
        schedule &&
        schedule.status == "Scheduled" &&
        schedule.isEnabled !== false
      );
    },
    canProcessAgentPayment(schedule) {
      return !!(schedule && schedule.isAgentPayment && schedule.needsProcessing);
    },
    paymentKey(payment) {
      if (payment && payment.txnID) return "txn-" + payment.txnID;
      return "pay-" + (payment && payment.paymentID);
    },
    scheduleKey(schedule) {
      if (schedule && schedule.isAgentPayment && schedule.txnID) {
        return "agent-" + schedule.txnID;
      }
      return "sch-" + (schedule && schedule.scheduleID);
    },
    relatedPaymentsForSchedule(scheduleId, scheduleDetailId, excludePaymentId) {
      return (this.payments || []).filter((p) => {
        if (!p) return false;
        if (excludePaymentId && p.paymentID === excludePaymentId) return false;
        if (scheduleId && p.scheduleID === scheduleId) return true;
        if (scheduleDetailId && p.scheduleDetailId === scheduleDetailId) {
          return true;
        }
        return false;
      });
    },
    async openPaymentDetails(payment) {
      this.paymentDetails = payment;
      this.paymentDetailsRelated = this.relatedPaymentsForSchedule(
        payment.scheduleID,
        payment.scheduleDetailId,
        payment.paymentID
      );
      this.paymentDetailsInstallments = [];
      this.paymentDetailsVisible = true;

      if (!payment.scheduleID) return;

      try {
        const details = await this.$http.get(
          "api/schedules/getScheduleDetails?scheduleID=" + payment.scheduleID
        );
        this.paymentDetailsInstallments = Array.isArray(details) ? details : [];
      } catch (e) {
        this.paymentDetailsInstallments = [];
      }
    },
    openScheduleDetails(schedule) {
      if (!schedule || schedule.isAgentPayment) return;

      this.selectedSchedule = Object.assign({}, schedule, {
        source: schedule.source || "ECC",
      });
      this.scheduleRelatedPayments = this.relatedPaymentsForSchedule(
        schedule.scheduleID
      );
      this.scheduleTab = 0;
      this.scheduleDialogVisible = true;
    },
    openPaymentFromSchedule(payment) {
      this.scheduleDialogVisible = false;
      this.openPaymentDetails(payment);
    },
    openScheduleFromPayment(payment) {
      const schedule = (this.schedules || []).find(
        (s) => s.scheduleID === payment.scheduleID
      );
      if (!schedule) {
        this.$notify({
          text: "Schedule was not found for this payment.",
          group: "notification",
          type: "warning",
        });
        return;
      }
      this.paymentDetailsVisible = false;
      this.openScheduleDetails(schedule);
    },
    openReverseFromDetails() {
      const payment = this.paymentDetails;
      this.paymentDetailsVisible = false;
      this.openReversePayment(payment);
    },
    openCancelSchedule(schedule) {
      this.scheduleToCancel = schedule;
      this.cancelScheduleDialog = true;
    },
    async confirmCancelSchedule(cancelAllRemaining) {
      const schedule = this.scheduleToCancel;
      if (!schedule) return;

      try {
        this.cancellingSchedule = true;

        if (schedule.isAgentPayment) {
          const txnIDs = cancelAllRemaining
            ? this.relatedAgentSchedules.map((row) => row.txnID).filter(Boolean)
            : [schedule.txnID].filter(Boolean);

          if (!txnIDs.length) {
            this.$notify({
              text: "There are no Agent Payments to cancel.",
              group: "notification",
              type: "warning",
            });
            this.cancelScheduleDialog = false;
            return;
          }

          await this.$http.post("api/businesses/cancelAgentPayments", {
            businessID: this.businessId,
            txnIDs,
          });
        } else {
          const details = await this.$http.get(
            "api/schedules/getScheduleDetails?scheduleID=" + schedule.scheduleID
          );
          const ids = (details || [])
            .filter((d) => d.status == "Scheduled")
            .map((d) => d.scheduleDetailID);

          if (!ids.length) {
            this.$notify({
              text: "There are no remaining scheduled payments to cancel.",
              group: "notification",
              type: "warning",
            });
            this.cancelScheduleDialog = false;
            return;
          }

          await this.$http.post("api/payments/cancelScheduledPayments", {
            accountID: this.billingAccountID,
            scheduleIDs: ids,
            user: this.$store.state.authentication.user,
          });
        }

        this.$notify({
          text: schedule.isAgentPayment
            ? cancelAllRemaining
              ? "Remaining Agent Payments canceled"
              : "Agent Payment canceled"
            : "Scheduled payments canceled",
          group: "notification",
          type: "success",
        });

        this.cancelScheduleDialog = false;
        this.scheduleDialogVisible = false;
        await this.loadPayments();
        await this.loadDetail();
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to cancel scheduled payments"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.cancellingSchedule = false;
      }
    },
    openProcessAgentPayments(rows) {
      const targets = (rows || []).filter(
        (row) => row && row.txnID && (row.needsProcessing || row.isAgentPayment)
      );

      if (!targets.length) return;

      this.processAgentTargets = targets;
      this.processAgentDialog = true;
    },
    async confirmProcessAgentPayments() {
      const txnIDs = (this.processAgentTargets || [])
        .map((row) => row.txnID)
        .filter((id) => id);

      if (!txnIDs.length) return;

      try {
        this.processingAgentPayments = true;
        this.processingAgentTxnId =
          txnIDs.length === 1 ? txnIDs[0] : this.processingAgentTxnId;

        await this.$http.post("api/businesses/processAgentPayments", {
          businessID: this.businessId,
          txnIDs,
        });

        this.$notify({
          text:
            txnIDs.length === 1
              ? "Agent Payment processed"
              : txnIDs.length + " Agent Payments processed",
          group: "notification",
          type: "success",
        });

        this.processAgentDialog = false;
        this.processAgentTargets = [];
        await this.loadPayments();
        await this.loadDetail();
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to process Agent Payment"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.processingAgentPayments = false;
        this.processingAgentTxnId = null;
      }
    },
    async reloadAfterScheduleChange() {
      this.scheduleDialogVisible = false;
      await this.loadPayments();
      await this.loadDetail();
    },
    openReversePayment(payment) {
      const source = payment.details || "";
      this.paymentToReverse = JSON.parse(JSON.stringify(payment));
      let details = source
        .split("|")
        .map((detail) => {
          const parts = detail.split(":");
          const code = (parts[0] || "").trim();
          const amount = parts[1];
          if (!+amount) return false;

          return {
            category: code === "ECC" ? "Employee Child Care" : code,
            amount,
            maxAmount: +amount,
          };
        })
        .filter(Boolean);

      if (!details.length) {
        details = [
          {
            category: "Employee Child Care",
            amount: payment.amount,
            maxAmount: +payment.amount,
          },
        ];
      }

      this.paymentToReverse.details = details;
      this.reversePaymentDialog = true;
    },
    closeReversePaymentDialog() {
      this.reversePaymentDialog = false;
    },
    async reloadAfterReverse() {
      this.reversePaymentDialog = false;
      await this.loadDetail();
    },
    async onTermChanged() {
      await this.loadDetail();
    },
    openEditDialog() {
      this.editForm = Object.assign(
        { businessID: this.businessId },
        this.business
      );
      this.editDialog = true;
    },
    async saveBusiness() {
      if (!this.editForm.name || !this.editForm.name.trim()) {
        this.$notify({
          text: "Business name is required",
          group: "notification",
          type: "error",
        });
        return;
      }

      try {
        this.savingBusiness = true;

        await this.$http.post("api/businesses/save", this.editForm);

        this.business = Object.assign({}, this.editForm);
        this.editDialog = false;

        this.$notify({
          text: "Business updated successfully",
          group: "notification",
          type: "success",
        });
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to save business"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.savingBusiness = false;
      }
    },
    openRemoveDialog() {
      if (this.hasLinkedAccounts) {
        this.$notify({
          text: "Unlink all families from this business before deleting it.",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.removeDialog = true;
    },
    async removeBusiness() {
      try {
        this.removingBusiness = true;

        await this.$http.post("api/businesses/delete?id=" + this.businessId);

        this.$notify({
          text: "Business deleted",
          group: "notification",
          type: "success",
        });

        this.removeDialog = false;
        this.$router.push({ name: "Businesses" });
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to delete business"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.removingBusiness = false;
      }
    },
    openLinkDialog() {
      this.editingExistingFamily = false;
      this.selectedAccount = null;
      this.linkStudents = [];
      this.selectedLinkStudentIds = [];
      this.linkCoverageStart = this.yearStart;
      this.linkCoverageEnd = this.yearEnd;
      this.linkDialogVisible = true;

      this.$nextTick(() => {
        if (this.$refs.accountLookup) {
          this.$refs.accountLookup.clear();
        }
      });
    },
    async openEditChildren(account) {
      this.editingExistingFamily = true;
      this.selectedAccount = {
        accountID: account.accountID,
        accountName: account.accountName,
      };
      this.linkCoverageStart = this.yearStart;
      this.linkCoverageEnd = this.yearEnd;
      this.linkDialogVisible = true;
      await this.loadLinkStudents(account.accountID, true);
    },
    async onAccountSelected(account) {
      this.selectedAccount = account;

      if (!account || !account.accountID) {
        this.linkStudents = [];
        this.selectedLinkStudentIds = [];

        return;
      }

      await this.loadLinkStudents(account.accountID, false);
    },
    async loadLinkStudents(accountId, selectLinked) {
      this.loadingLinkStudents = true;
      this.linkStudents = [];
      this.selectedLinkStudentIds = [];

      try {
        const response = await this.$http.get(
          "api/businesses/getLinkableStudents?businessId=" +
            this.businessId +
            "&accountId=" +
            encodeURIComponent(accountId) +
            "&term=" +
            encodeURIComponent(this.term)
        );

        this.linkStudents = ((response && response.students) || []).map(
          (student) => ({
            ...student,
            coverageStartDate:
              this.toDateInput(student.coverageStartDate) ||
              this.toDateInput(response.yearStart) ||
              this.yearStart,
            coverageEndDate:
              this.toDateInput(student.coverageEndDate) ||
              this.toDateInput(response.yearEnd) ||
              this.yearEnd,
            startMenu: false,
            endMenu: false,
          })
        );
        if (response && response.yearStart) {
          this.linkCoverageStart = this.toDateInput(response.yearStart);
          this.linkCoverageEnd = this.toDateInput(response.yearEnd);
        }

        this.selectedLinkStudentIds = this.linkStudents
          .filter((s) => (selectLinked ? s.selected : !s.linkedToOtherBusiness))
          .map((s) => this.normalizeStudentId(s.studentID));
      } catch (e) {
        this.linkStudents = [];
      } finally {
        this.loadingLinkStudents = false;
      }
    },
    async confirmLinkAccount() {
      if (!this.canConfirmLink) return;

      for (let i = 0; i < this.selectedLinkStudentIds.length; i++) {
        const id = this.selectedLinkStudentIds[i];
        const student = this.linkStudents.find(
          (s) => this.normalizeStudentId(s.studentID) === id
        );
        const error = this.coverageRangeError(
          (student && student.coverageStartDate) || this.linkCoverageStart,
          (student && student.coverageEndDate) || this.linkCoverageEnd
        );
        if (error) {
          this.$notify({
            text:
              error +
              (student
                ? " (" + (student.engName || student.hebName || id) + ")"
                : ""),
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      try {
        this.linking = true;

        await this.$http.post("api/businesses/linkAccount", {
          businessID: this.businessId,
          accountID: this.selectedAccount.accountID,
          term: this.term,
          students: this.selectedLinkStudentIds.map((id) => {
            const student = this.linkStudents.find(
              (s) => this.normalizeStudentId(s.studentID) === id
            );
            return {
              studentID: id,
              coverageStartDate:
                (student && student.coverageStartDate) ||
                this.linkCoverageStart,
              coverageEndDate:
                (student && student.coverageEndDate) || this.linkCoverageEnd,
            };
          }),
        });

        this.$notify({
          text: this.editingExistingFamily
            ? "Covered children updated"
            : "Family linked successfully",
          group: "notification",
          type: "success",
        });

        this.linkDialogVisible = false;

        await this.loadDetail();
        await this.loadTerms();
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to link this account"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.linking = false;
      }
    },
    applyLinkCoverageToSelected() {
      this.linkStudents.forEach((student) => {
        if (!this.isLinkStudentSelected(student)) return;
        student.coverageStartDate = this.linkCoverageStart;
        student.coverageEndDate = this.linkCoverageEnd;
      });
    },
    onLinkStudentToggled(student) {
      this.$nextTick(() => {
        if (!this.isLinkStudentSelected(student) || student.selected) return;
        student.coverageStartDate = this.linkCoverageStart;
        student.coverageEndDate = this.linkCoverageEnd;
      });
    },
    openCoverageDialog(account, student) {
      this.coverageError = "";
      this.coverageForm = {
        account: account,
        student: student,
        name: (student && (student.engName || student.hebName)) || "",
        startDate:
          this.toDateInput(student.coverageStartDate) || this.yearStart,
        endDate: this.toDateInput(student.coverageEndDate) || this.yearEnd,
      };
      this.coverageDialogVisible = true;
    },
    async saveCoverageDialog() {
      const error = this.coverageRangeError(
        this.coverageForm.startDate,
        this.coverageForm.endDate
      );
      if (error) {
        this.coverageError = error;
        return;
      }

      try {
        this.savingCoverage = true;
        await this.saveStudentCoverage(
          this.coverageForm.account,
          this.coverageForm.student,
          this.coverageForm.startDate,
          this.coverageForm.endDate
        );
        this.coverageDialogVisible = false;
        await this.loadDetail();
        this.$notify({
          text: "Coverage dates updated",
          group: "notification",
          type: "success",
        });
      } catch (e) {
        this.coverageError = this.getErrorMessage(
          e,
          "Failed to update coverage dates"
        );
      } finally {
        this.savingCoverage = false;
      }
    },
    async saveStudentCoverage(account, student, startDate, endDate) {
      const studentId = this.normalizeStudentId(student && student.studentID);
      const response = await this.$http.post("api/businesses/saveCoverage", {
        businessID: this.businessId,
        accountID: String((account && account.accountID) || "").trim(),
        studentID: studentId,
        term: this.term,
        coverageStartDate: startDate,
        coverageEndDate: endDate,
      });

      if (response && student) {
        Object.assign(student, response);
      }

      return response;
    },
    unlinkAccount(account) {
      this.unlinkTarget = account;
      this.unlinkConfirmVisible = true;
    },
    async confirmUnlinkAccount() {
      const account = this.unlinkTarget;
      if (!account) return;

      try {
        this.unlinkingId = account.linkID;

        await this.$http.post(
          "api/businesses/unlinkAccount?linkId=" + account.linkID
        );

        this.$notify({
          text: "Family unlinked",
          group: "notification",
          type: "success",
        });

        this.unlinkConfirmVisible = false;

        await this.loadDetail();
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to unlink this account"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.unlinkingId = null;
      }
    },
    openUnlinkStudentDialog(account, student) {
      this.unlinkStudentTarget = { account, student };
      this.unlinkStudentConfirmVisible = true;
    },
    async confirmUnlinkStudent() {
      const target = this.unlinkStudentTarget;
      if (!target || !target.account || !target.student) return;

      await this.unlinkStudent(target.account, target.student);
    },
    async unlinkStudent(account, student) {
      try {
        this.unlinkingStudentId = student.studentID;

        await this.$http.post("api/businesses/unlinkStudent", {
          businessID: this.businessId,
          accountID: account.accountID,
          studentID: student.studentID,
          term: this.term,
        });

        this.$notify({
          text: "Child removed from the employer bill",
          group: "notification",
          type: "success",
        });

        this.unlinkStudentConfirmVisible = false;
        this.unlinkStudentTarget = null;

        await this.loadDetail();
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to unlink this child"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.unlinkingStudentId = null;
      }
    },
    openRateDialog(account) {
      this.rateDialogAccount = account;
      this.rateError = "";
      this.bulkRateType = null;
      this.bulkMonthlyRate = null;
      this.rateForm = (account.students || []).map((s) => {
        const monthly =
          s.rateType === "Custom"
            ? s.monthlyRate != null
              ? s.monthlyRate
              : this.annualToMonthly(s.customAnnualRate)
            : null;
        return {
          studentID: s.studentID,
          name: s.engName || s.hebName || s.studentID,
          rateType: s.rateType,
          customMonthlyRate: monthly,
          coverageStartDate:
            this.toDateInput(s.coverageStartDate) || this.yearStart,
          coverageEndDate: this.toDateInput(s.coverageEndDate) || this.yearEnd,
          startMenu: false,
          endMenu: false,
        };
      });
      this.rateDialogVisible = true;
    },
    onRowRateTypeChanged(row) {
      if (row.rateType !== "Custom") {
        row.customMonthlyRate = null;
        return;
      }
      if (row.customMonthlyRate != null && row.customMonthlyRate !== "") return;

      const student = this.findStudent(this.rateDialogAccount, row.studentID);
      if (student && student.monthlyRate != null) {
        row.customMonthlyRate = student.monthlyRate;
      }
    },
    applyRateToAll() {
      if (!this.bulkRateType) return;

      this.rateForm.forEach((row) => {
        row.rateType = this.bulkRateType;
        row.customMonthlyRate =
          this.bulkRateType === "Custom" ? this.bulkMonthlyRate : null;
      });
    },
    isRateRowChanged(row, student) {
      if (!student) return true;
      if (row.rateType !== student.rateType) return true;
      if (row.rateType === "Custom") {
        return (
          Number(student.customAnnualRate) !==
          this.monthlyToAnnual(row.customMonthlyRate)
        );
      }
      return false;
    },
    isCoverageRowChanged(row, student) {
      if (!student) return true;
      return (
        !this.datesEqual(row.coverageStartDate, student.coverageStartDate) ||
        !this.datesEqual(row.coverageEndDate, student.coverageEndDate)
      );
    },
    async saveRates() {
      const account = this.rateDialogAccount;
      if (!account) return;

      this.rateError = "";

      for (let i = 0; i < this.rateForm.length; i++) {
        const row = this.rateForm[i];
        if (row.rateType !== "Custom") continue;

        const monthly = Number(row.customMonthlyRate);
        if (
          row.customMonthlyRate == null ||
          row.customMonthlyRate === "" ||
          isNaN(monthly) ||
          monthly <= 0
        ) {
          this.rateError =
            "A custom monthly rate is required for " + row.name + ".";
          return;
        }
      }

      for (let i = 0; i < this.rateForm.length; i++) {
        const row = this.rateForm[i];
        const error = this.coverageRangeError(
          row.coverageStartDate,
          row.coverageEndDate
        );
        if (error) {
          this.rateError = error + " (" + row.name + ")";
          return;
        }
      }

      const rateChanged = this.rateForm.filter((row) =>
        this.isRateRowChanged(row, this.findStudent(account, row.studentID))
      );
      const coverageChanged = this.rateForm.filter((row) =>
        this.isCoverageRowChanged(row, this.findStudent(account, row.studentID))
      );

      if (!rateChanged.length && !coverageChanged.length) {
        this.rateDialogVisible = false;
        return;
      }

      try {
        this.savingRates = true;

        for (let i = 0; i < rateChanged.length; i++) {
          const row = rateChanged[i];
          const student = this.findStudent(account, row.studentID);
          const studentId = this.normalizeStudentId(
            (student && student.studentID) || row.studentID
          );

          const response = await this.$http.post("api/businesses/savePricing", {
            businessID: this.businessId,
            accountID: String(account.accountID || "").trim(),
            studentID: studentId,
            term: this.term,
            rateType: row.rateType,
            customAnnualRate:
              row.rateType === "Custom"
                ? this.monthlyToAnnual(row.customMonthlyRate)
                : null,
          });

          if (response && student) {
            Object.assign(student, response);
          }
        }

        for (let i = 0; i < coverageChanged.length; i++) {
          const row = coverageChanged[i];
          const student = this.findStudent(account, row.studentID);
          await this.saveStudentCoverage(
            account,
            student || { studentID: row.studentID },
            row.coverageStartDate,
            row.coverageEndDate
          );
        }

        this.rateDialogVisible = false;
        await this.loadDetail();

        this.$notify({
          text: coverageChanged.length
            ? "Rates and coverage updated successfully"
            : "Rates updated successfully",
          group: "notification",
          type: "success",
        });
      } catch (e) {
        this.rateError = this.getErrorMessage(e, "Failed to update pricing");
        this.$notify({
          text: this.rateError,
          group: "notification",
          type: "error",
        });
      } finally {
        this.savingRates = false;
      }
    },
    recomputeTotals() {
      this.accounts.forEach((account) => {
        account.annualTotal = account.students.reduce(
          (sum, s) => sum + (s.contractAmount || s.annualRate || 0),
          0
        );
        account.monthlyTotal = account.students.reduce(
          (sum, s) => sum + (s.monthlyRate || 0),
          0
        );
      });

      this.annualTotal = this.accounts.reduce(
        (sum, a) => sum + a.annualTotal,
        0
      );
      this.monthlyTotal = this.accounts.reduce(
        (sum, a) => sum + a.monthlyTotal,
        0
      );
    },
    async openPayDialog() {
      try {
        const ensured = await this.$http.post(
          "api/businesses/ensureBillingAccount?businessId=" + this.businessId
        );
        this.billingAccountID =
          (ensured && ensured.billingAccountID) || this.billingAccountID;
        if (ensured && ensured.balance != null) {
          this.balance = ensured.balance;
        }
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(
            e,
            "Could not create a billing account for this business"
          ),
          group: "notification",
          type: "error",
        });
        return;
      }

      if (!this.billingAccountID) {
        this.$notify({
          text: "A billing account is required before taking payment.",
          group: "notification",
          type: "error",
        });
        return;
      }

      await this.loadSavedCards();
      this.payDialogVisible = true;

      this.$nextTick(() => {
        const dialog = this.$refs.paymentDialog;
        if (!dialog) return;

        dialog.emailAddress = (this.business && this.business.email) || "";
        dialog.nameOnAccount = (this.business && this.business.name) || "";
        if (dialog.updateEmailMessage) {
          dialog.updateEmailMessage();
        }
      });
    },
    closePaymentDialog() {
      this.payDialogVisible = false;
    },
    async loadSavedCards() {
      this.paymentMethods = [];

      if (!this.billingAccountID) return;

      try {
        const cards = await this.$http.get(
          "api/payments/getSavedCards?accountID=" +
            encodeURIComponent(this.billingAccountID)
        );
        this.paymentMethods = (cards || []).map((method) => {
          method.cardInfo =
            (method.cardType || method.type || "Card") +
            " - " +
            (method.cardNumberLast5 || "");
          return method;
        });
      } catch (e) {
        this.paymentMethods = [];
      }
    },
    async downloadContract() {
      try {
        this.downloadingContract = true;

        const response = await this.$http({
          url:
            "api/businesses/downloadContract?businessId=" +
            this.businessId +
            "&term=" +
            encodeURIComponent(this.term),
          method: "GET",
          responseType: "blob",
        });

        this.triggerDownload(
          response,
          this.documentFileName("Employer_Childcare_Contract")
        );
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to download contract"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.downloadingContract = false;
      }
    },
    setSignedContract(hasFile, fileName, uploadedAt, uploadedBy) {
      this.hasSignedContract = !!hasFile;
      this.signedContractFileName = (hasFile && fileName) || "";
      this.signedContractUploadedAt = (hasFile && uploadedAt) || null;
      this.signedContractUploadedBy = (hasFile && uploadedBy) || "";
    },
    pickSignedContract() {
      if (this.$refs.signedContractFile) {
        this.$refs.signedContractFile.click();
      }
    },
    async onSignedContractPicked(e) {
      const input = e.target;
      const file = input.files && input.files[0];
      input.value = "";

      if (!file) return;
      if (!/\.pdf$/i.test(file.name || "")) {
        this.$notify({
          text: "The signed contract must be a PDF.",
          group: "notification",
          type: "error",
        });
        return;
      }

      try {
        this.uploadingSignedContract = true;
        const formData = new FormData();
        formData.append("file", file);

        const response = await this.$http.post(
          "api/businesses/uploadSignedContract?businessId=" +
            this.businessId +
            "&term=" +
            encodeURIComponent(this.term),
          formData,
          { notifyError: false }
        );

        this.setSignedContract(
          true,
          (response && response.fileName) || file.name,
          response && response.uploadedAt,
          response && response.uploadedBy
        );
        this.$notify({
          text: "Signed contract saved.",
          group: "notification",
          type: "success",
        });
      } catch (err) {
        this.$notify({
          text: this.getErrorMessage(err, "Failed to save the signed contract"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.uploadingSignedContract = false;
      }
    },
    async viewSignedContract() {
      const popup = window.open("", "_blank");
      try {
        this.openingSignedContract = true;
        const response = await this.$http.get(
          "api/businesses/getSignedContractUrl?businessId=" +
            this.businessId +
            "&term=" +
            encodeURIComponent(this.term),
          { notifyError: false }
        );
        const url = response && response.url;
        if (!url) {
          throw new Error("No signed contract is attached for this term.");
        }
        if (popup) {
          popup.location.href = url;
        } else {
          window.open(url, "_blank");
        }
      } catch (err) {
        if (popup) popup.close();
        this.$notify({
          text: this.getErrorMessage(err, "Failed to open the signed contract"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.openingSignedContract = false;
      }
    },
    async confirmRemoveSignedContract() {
      try {
        this.removingSignedContract = true;
        await this.$http.post(
          "api/businesses/removeSignedContract?businessId=" +
            this.businessId +
            "&term=" +
            encodeURIComponent(this.term),
          null,
          { notifyError: false }
        );
        this.setSignedContract(false);
        this.removeSignedContractDialog = false;
        this.$notify({
          text: "Signed contract removed.",
          group: "notification",
          type: "success",
        });
      } catch (err) {
        this.$notify({
          text: this.getErrorMessage(
            err,
            "Failed to remove the signed contract"
          ),
          group: "notification",
          type: "error",
        });
      } finally {
        this.removingSignedContract = false;
      }
    },
    async downloadInvoices() {
      try {
        this.downloadingInvoices = true;

        const response = await this.$http({
          url:
            "api/businesses/downloadInvoices?businessId=" +
            this.businessId +
            "&term=" +
            encodeURIComponent(this.term),
          method: "GET",
          responseType: "blob",
        });

        this.triggerDownload(
          response,
          this.documentFileName("Childcare_Statements")
        );
      } catch (e) {
        this.$notify({
          text: this.getErrorMessage(e, "Failed to download invoices"),
          group: "notification",
          type: "error",
        });
      } finally {
        this.downloadingInvoices = false;
      }
    },
    documentFileName(documentType) {
      const skip = {
        INC: true,
        LLC: true,
        LTD: true,
        LLP: true,
        PC: true,
        CORP: true,
        CO: true,
        THE: true,
        AND: true,
        OF: true,
        INCORPORATED: true,
      };
      const words = String((this.business && this.business.name) || "Business")
        .trim()
        .split(/[^A-Za-z0-9]+/)
        .filter((w) => w && !skip[w.toUpperCase()]);
      const namePart =
        words
          .map((w) => {
            if (w.length <= 3 && w === w.toUpperCase()) return w;
            return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
          })
          .join("_") || "Business";

      const digits = String(this.term || "").replace(/\D/g, "");
      const endYear =
        digits.length >= 4 ? parseInt(digits.slice(-4), 10) : null;
      const range = endYear ? endYear - 1 + "-" + endYear : this.term;

      return namePart + "_" + documentType + "_" + range + ".pdf";
    },
    triggerDownload(blobResponse, fileName) {
      const url = window.URL.createObjectURL(new Blob([blobResponse]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    getErrorMessage(error, fallback) {
      if (error && error.Message) return error.Message;
      if (error && error.message) return error.message;
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.Message
      )
        return error.response.data.Message;

      return fallback;
    },
  },
};
</script>

<style>
.biz-card {
  min-height: 400px;
}
.biz-header-title {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #fff;
}
.biz-header-account {
  margin-left: 10px;
  font-size: 14px;
  font-weight: 500;
  opacity: 0.85;
}
.biz-agent-display {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 140px;
  line-height: 1.2;
}
.biz-agent-display-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  opacity: 0.75;
}
.biz-agent-display-name {
  font-size: 14px;
  font-weight: 600;
}
.biz-term-switcher {
  min-width: unset;
}
.biz-term-list {
  display: flex;
  align-items: center;
  gap: 6px;
}
.biz-term-chip {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: underline;
}
.biz-term-chip:hover {
  color: #fff;
}
.biz-term-chip--active {
  font-size: 14px;
  color: #fff;
  text-decoration: none;
  cursor: default;
}
.biz-section-tabs {
  margin: 4px 0 12px;
}
.biz-body {
  padding: 16px 20px 24px;
}
.biz-info-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  border-bottom: 1px solid #eee;
  padding-bottom: 12px;
  margin-bottom: 12px;
  gap: 16px;
}
.biz-info-item {
  flex: 1 1 0;
  min-width: 0;
}
.biz-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #757575;
}
.biz-value {
  font-size: 14px;
  font-weight: 500;
  color: #212121;
}
.biz-summary-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.biz-summary-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f3fbf4;
  border: 1px solid #dcedc8;
  border-radius: 6px;
  padding: 8px 8px;
  min-height: 58px;
  text-align: center;
  flex: 1 1 100px;
  min-width: 0;
}
.biz-summary-card--wide {
  flex: 2.6 1 260px;
  flex-direction: row;
  justify-content: center;
  gap: 18px;
}
.biz-summary-main {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.biz-summary-num {
  font-size: 19px;
  font-weight: 700;
  color: #2e7d32;
  line-height: 1.2;
}
.biz-summary-label {
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #757575;
  margin-top: 1px;
}
.biz-summary-card--payment {
  background: #eef4fd;
  border: 1px dashed #90caf9;
  flex: 0.9 1 100px;
}
.biz-summary-card--payment .biz-summary-num {
  color: #1565c0;
}
.biz-summary-sub {
  font-size: 10px;
  line-height: 1.15;
  color: #756c6c;
  margin-top: 3px;
  white-space: nowrap;
}
.biz-summary-card--wide .biz-summary-sub {
  margin-top: 0;
  padding-left: 18px;
  border-left: 1px solid #c5e1a5;
  text-align: left;
}
.biz-summary-card--wide .biz-summary-sub div + div {
  margin-top: 4px;
}
.biz-actions-row {
  align-items: center;
  margin: 10px 0 18px;
}
.biz-actions-row .v-btn {
  margin-left: 0;
  margin-right: 10px;
}
.biz-download-btn {
  font-size: 11px;
}
.biz-download-btn .v-icon {
  font-size: 16px;
}
.biz-signed-pill {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 2px 0 10px;
  margin-right: 10px;
  border: 1px solid #c8e6c9;
  border-radius: 14px;
  background: #f6fbf6;
  font-size: 12px;
}
.biz-signed-pill-name {
  font-weight: 500;
  color: #2e7d32;
  cursor: pointer;
}
.biz-signed-pill-name:hover {
  text-decoration: underline;
}
.biz-signed-pill-divider {
  width: 1px;
  height: 16px;
  margin: 0 4px 0 8px;
  background: #c8e6c9;
}
.biz-actions-row .biz-signed-pill .biz-signed-pill-btn {
  width: 24px;
  height: 24px;
  margin: 0;
}
.biz-accounts-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  padding: 12px 16px 4px;
  margin-bottom: 18px;
}
.biz-accounts-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0 0 6px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}
.biz-accounts-title {
  font-size: 14px;
  font-weight: 600;
  color: #212121;
}
.biz-accounts-count {
  font-size: 12px;
  color: #757575;
}
.biz-accounts-panel {
  box-shadow: none;
}
.container.grid-list-xl .biz-accounts-panel .v-expansion-panel__header .layout {
  margin-right: 0;
}
.biz-accounts-panel .v-expansion-panel__header__icon {
  margin-left: 10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e8f5e9;
  display: flex;
  align-items: center;
  justify-content: center;
}
.biz-accounts-panel .v-expansion-panel__header__icon .v-icon {
  color: #2e7d32 !important;
  font-size: 24px;
}
.biz-edit-rates-btn {
  margin-right: 16px;
}
.biz-account-name {
  font-weight: 600;
  color: #212121;
}
.biz-account-id {
  color: #9e9e9e;
  font-size: 12px;
  margin-left: 6px;
}
.biz-account-total {
  font-size: 12.5px;
  color: #2e7d32;
  font-weight: 600;
}
.biz-students-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.biz-students-table thead th {
  background: #f1f8e9;
  color: #33691e;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 8px 10px;
  text-align: left;
  border-bottom: 2px solid #dcedc8;
}
.biz-students-table tbody td {
  padding: 6px 10px;
  border-bottom: 1px solid #f1f8e9;
}
.biz-td-name {
  font-weight: 600;
}
.biz-td-empty {
  text-align: center;
  padding: 16px !important;
  color: #9e9e9e;
  font-style: italic;
}
.biz-muted {
  color: #90a4ae;
  white-space: normal;
}
.biz-dialog-body {
  padding: 0 24px 16px;
  color: #212121;
  font-size: 14px;
}
.biz-schedule-subtitle {
  font-size: 13px;
  color: #757575;
  margin: -4px 0 14px;
}
.biz-schedule-pill {
  font-size: 12px;
  font-weight: 600;
  color: #33691e;
  background: #f1f8e9;
  border: 1px solid #dcedc8;
  border-radius: 12px;
  padding: 4px 10px;
}
.biz-schedule-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 24px;
  margin-bottom: 12px;
}
.biz-schedule-field {
  font-size: 13px;
  color: #212121;
}
.biz-schedule-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9e9e9e;
  margin-bottom: 2px;
}
.biz-schedule-tabs {
  margin: 0 -24px;
  box-shadow: inset 0 -1px 0 #eeeeee;
}
.biz-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: #eeeeee;
  color: #757575;
}
.biz-tab-count-alert {
  background: #ffebee;
  color: #c62828;
}
.biz-account-footer {
  padding: 8px 10px;
  text-align: right;
}
.biz-account-details,
.biz-invoice-rundown {
  margin-top: 14px;
  padding: 12px 10px 4px;
  border-top: 1px solid #f1f8e9;
}
.biz-invoice-line {
  line-height: 1.5;
}
.biz-empty-accounts {
  text-align: center;
  color: #9e9e9e;
  font-style: italic;
  padding: 30px 0;
}

/* ── Ledger ── */
.biz-ledger-card {
  border: 1px solid #dcedc8;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 8px;
  background: #fbfffb;
}
.biz-ledger-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.biz-ledger-title-group {
  display: flex;
  align-items: center;
}
.biz-ledger-title {
  font-size: 15px;
  font-weight: 600;
  color: #33691e;
}
.biz-ledger-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}
.biz-ledger-table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #78909c;
  padding: 6px 12px 8px;
  border-bottom: 2px solid #c8e6c9;
}
.biz-ledger-table th.biz-ledger-amount,
.biz-ledger-table th.biz-ledger-running {
  text-align: right;
}
.biz-ledger-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e8f5e9;
  vertical-align: top;
}
.biz-ledger-table tbody tr:hover {
  background: #f4faf4;
}
.biz-ledger-date {
  width: 110px;
  color: #546e7a;
  white-space: nowrap;
  font-weight: 500;
}
.biz-ledger-desc {
  font-weight: 600;
  color: #212121;
}
.biz-ledger-desc--bill {
  color: #37474f;
}
.biz-ledger-desc--payment {
  color: #1b5e20;
}
.biz-ledger-desc--scheduled {
  color: #ef6c00;
}
.biz-ledger-amount {
  width: 120px;
  text-align: right;
  font-weight: 600;
  white-space: nowrap;
}
.biz-ledger-amount--charge {
  color: #c62828;
}
.biz-ledger-amount--payment {
  color: #2e7d32;
}
.biz-ledger-amount--scheduled {
  color: #ef6c00;
}
.biz-ledger-running {
  width: 150px;
  text-align: right;
  font-size: 15px;
  font-weight: 700;
  color: #1b5e20;
  white-space: nowrap;
  background: #e8f5e9;
}

/* ── Manage Agents table ── */
.biz-agents-table {
  width: 100%;
  border-collapse: collapse;
}
.biz-agents-table th {
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #757575;
  padding: 4px 8px;
  border-bottom: 2px solid #e0e0e0;
}
.biz-agents-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
}

.biz-payments-section {
  margin-top: 8px;
}
.biz-payments-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: #33691e;
  margin-bottom: 10px;
}
.biz-clickable-row {
  cursor: pointer;
}
.biz-clickable-row:hover {
  background: #f1f8e9;
}
.biz-dialog-actions {
  flex-wrap: wrap !important;
  justify-content: flex-end;
}
.biz-dialog-actions .v-btn {
  margin: 4px 0 4px 8px !important;
}
.biz-cancel-summary {
  border: 1px solid #eeeeee;
  border-radius: 6px;
  background: #fafafa;
  padding: 10px 12px;
  margin-bottom: 16px;
}
.biz-cancel-summary-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #90a4ae;
  margin-bottom: 4px;
}
.biz-cancel-summary-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 600;
  color: #212121;
}
.biz-cancel-summary-amount {
  color: #2e7d32;
}
.biz-cancel-choices {
  margin-bottom: 4px;
}
.biz-cancel-hint {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.4;
}
.biz-row-pastdue {
  background: #fff8e1;
}
.biz-pastdue {
  color: #e65100;
  font-weight: 600;
}
.biz-ledger-desc--pastdue,
.biz-ledger-amount--pastdue {
  color: #e65100;
}
.assign-account-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #9e9e9e;
  font-style: italic;
}
.biz-rate-dialog-account {
  font-weight: 500;
  color: #616161;
}
.biz-rate-bulk {
  background: #f3fbf4;
  border: 1px solid #dcedc8;
  border-radius: 8px;
  padding: 12px 14px 16px;
  margin: 8px 0 16px;
}
.biz-rate-list {
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;
}
.biz-rate-row {
  padding: 8px 0 10px;
  border-bottom: 1px solid #eee;
}
.biz-rate-row:last-of-type {
  border-bottom: none;
}
.biz-rate-row-name {
  font-weight: 600;
  color: #212121;
  margin-bottom: 8px;
}
.biz-rate-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.biz-rate-fields .v-input,
.biz-rate-fields .v-menu {
  margin-top: 0;
  margin-bottom: 0;
}
.biz-link-students {
  max-height: 380px;
  overflow-y: auto;
  border: 1px solid #e8f5e9;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 12px;
}
.biz-link-student-row {
  border-bottom: 1px solid #f1f8e9;
  padding: 8px 0;
}
.biz-link-student-row:last-child {
  border-bottom: none;
}
.biz-link-student-dates {
  padding: 4px 0 8px 32px;
}
.biz-coverage-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}
.biz-link-account-name {
  font-weight: 600;
  color: #2e7d32;
}
</style>
